import { Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Popover } from '@mui/material';
import './evScreenTable.css';
import { formatPrice } from '../../../utils/formatPrice';
import { EVPercent } from '../../../components/EVPercent';
import { GameDiv } from '../../../components/GameDiv';
import { BetPrice } from '../../../components/BetPrice';
import { BookAndLink } from './BookAndLink';
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { devigOddsProbit, getComparator, getLineMovementDirection, calculateOverallConfidence, calculateConfidenceWithBreakdown, calculateSharpBookCoverage, convertDecimalToAmerican } from '../utils';
import { BetPriceDirection } from './BetPriceDirection';
import { getRelatedOdds } from '../../../utils/api/getRelatedOdds';
import { RelatedOddsDropdown } from './RelatedOddsDropdown';
import { useAuth0 } from '@auth0/auth0-react';
import { removeBetPlaced, setBetPlaced } from '../../../utils/api/setBetPlaced';
import axios from 'axios';
import { MobileEVCard } from './MobileEVCard';
import { UpgradeModal } from '../../../components/UpgradeModal/UpgradeModal';
import { HelpOutline } from '@mui/icons-material';
import { ConfidenceDisplay } from './ConfidenceDisplay';

export function addPlusSign(number) {
	if (number > 0) {
		return `+${number}`;
	} else {
		return number;
	}
}

export function removeObjectWithProperty(array, property, value) {
	return array.filter((obj) => obj[property] !== value);
}

// Helper to format sportsbook deep links using the unified deep_link structure
// Handles both new format: { ios, android, desktop } and old format: string or { deep_link: { desktop } }
function formatDeepLink(linkObj) {
    if (!linkObj) return '';
    
    // Handle new format: { ios, android, desktop }
    if (typeof linkObj === 'object' && linkObj !== null && linkObj.desktop) {
        const desktopLink = linkObj.desktop;
        if (desktopLink.includes('betmgm')) {
            return desktopLink.replace(/<STATE>./g, 'on.').replace(/\.com/g, '.ca');
        }
        return desktopLink.replace(/<STATE>./g, '');
    }
    
    // Handle old format: { deep_link: { desktop } }
    if (linkObj?.deep_link?.desktop) {
        const desktopLink = linkObj.deep_link.desktop;
        if (desktopLink.includes('betmgm')) {
            return desktopLink.replace(/<STATE>./g, 'on.').replace(/\.com/g, '.ca');
        }
        return desktopLink.replace(/<STATE>./g, '');
    }
    
    // Handle string format (old format)
    if (typeof linkObj === 'string') {
        if (linkObj.includes('betmgm')) {
            return linkObj.replace(/<STATE>./g, 'on.').replace(/\.com/g, '.ca');
        }
        return linkObj.replace(/<STATE>./g, '');
    }
    
    return '';
}

// Helper to format sharp deep links (no BetMGM CA handling previously)
// Handles both new format: { ios, android, desktop } and old format: { deep_link: { desktop } }
function formatSharpDeepLink(linkObj) {
    // Handle new format: { ios, android, desktop }
    let desktopLink = linkObj?.desktop;
    // Handle old format: { deep_link: { desktop } }
    if (!desktopLink && linkObj?.deep_link?.desktop) {
        desktopLink = linkObj.deep_link.desktop;
    }
    if (!desktopLink) return '';
    return desktopLink.replace(/<STATE>./g, '');
}

export const EVScreenTable = (props) => {
	const { data, isLive, isAutoRefresh, tutorial, isLoading, bankroll, relatedOdds, setRelatedOdds, preloadedRelatedOdds, selectedRow, setSelectedRow, allBetsOpen, loadingIcon, isMobileProp, order, setOrder, orderBy, setOrderBy, sharpFilter, freeRowLimit } = props;
	const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
	const { user } = useAuth0();
	const [initialized, setInitialized] = useState(false);

	const [placedBets, setPlacedBets] = useState([]);
	const [preloadedDataVersion, setPreloadedDataVersion] = useState(0);
	const [isPreloadingData, setIsPreloadingData] = useState(true);
	const [amountPopoverAnchor, setAmountPopoverAnchor] = useState(null);
	const [amountPopoverRowId, setAmountPopoverRowId] = useState(null);
	const hoverTimeoutRef = useRef(null);
	const popoverRef = useRef(null);

	// Price history lazy loading state - only fetches for rows visible in viewport
	const [priceHistoryCacheVersion, setPriceHistoryCacheVersion] = useState(0);
	const priceHistoryCacheRef = useRef({});
	const pendingFetchRef = useRef(new Map());
	const attemptedFetchRef = useRef(new Set()); // Track IDs we've attempted to fetch (including failures)
	const fetchTimerRef = useRef(null);
	const rowObserverRef = useRef(null);
	const dataRef = useRef(data); // Keep data ref for use in callbacks

	// Related odds lazy loading state - only fetches for rows visible in viewport
	const [relatedOddsCacheVersion, setRelatedOddsCacheVersion] = useState(0);
	const relatedOddsCacheRef = useRef(new Map()); // outcome_id_normalized_no_book -> relatedOdds array
	const pendingRelatedOddsFetchRef = useRef(new Map()); // outcome_id_normalized_no_book -> { sport, options }
	const attemptedRelatedOddsFetchRef = useRef(new Set()); // Track IDs we've attempted to fetch
	const relatedOddsFetchTimerRef = useRef(null);

	const [isMobile, setIsMobile] = useState(window.innerWidth <= 750);

	const userdb = JSON.parse(window.localStorage.getItem('userdb'));
	const isMaster =
		userdb?.stripe_plan?.nickname?.includes('Visionary') &&
		userdb?.stripe_plan.active &&
		userdb?.stripe_subscription !== null &&
		userdb?.stripe_status !== 'deleted' &&
		userdb.stripe_status !== 'past_due';
	const isFree = userdb?.stripe_subscription === null || !userdb?.stripe_subscription || userdb?.stripe_status === 'deleted' || userdb?.stripe_status === 'past_due';

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth >= 750) {
				setIsMobile(false);
			} else {
				setIsMobile(true);
			}
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Effect to trigger re-render when preloadedRelatedOdds changes
	useEffect(() => {
		if (preloadedRelatedOdds && preloadedRelatedOdds.size > 0) {
			// console.log('PreloadedRelatedOdds updated, triggering confidence recalculation. Size:', preloadedRelatedOdds.size);
			setPreloadedDataVersion(prev => prev + 1);
			setIsPreloadingData(false); // Data is now available
		} else if (preloadedRelatedOdds && preloadedRelatedOdds.size === 0) {
			// If we have an empty Map, it means preloading is complete but no data was found
			setIsPreloadingData(false);
		}
	}, [preloadedRelatedOdds]);

	// Debounced batch fetch for price history of visible rows
	const schedulePriceHistoryFetch = useCallback(() => {
		if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
		fetchTimerRef.current = setTimeout(async () => {
			const pending = new Map(pendingFetchRef.current);
			pendingFetchRef.current.clear();
			if (pending.size === 0) return;

			const outcomeIds = Array.from(pending.keys());
			const sports = [...new Set(Array.from(pending.values()))];

			try {
				const accessToken = window.localStorage.getItem('access_token');
				if (!accessToken) {
					// Mark all as attempted even if we can't fetch (no auth)
					outcomeIds.forEach(id => attemptedFetchRef.current.add(id));
					return;
				}
				const historyRes = await axios.get(
					`${process.env.REACT_APP_BACKEND_URL}/outcomes_history?outcome_id_normalized=${outcomeIds.join(',')}&sport=${sports.join(',')}`,
					{ headers: { Authorization: `Bearer ${accessToken}` } }
				);
				
				// Mark all requested IDs as attempted
				outcomeIds.forEach(id => attemptedFetchRef.current.add(id));
				
				if (Array.isArray(historyRes.data)) {
					const returnedIds = new Set();
					for (const entry of historyRes.data) {
						if (entry.outcome_id_normalized) {
							priceHistoryCacheRef.current[entry.outcome_id_normalized] = entry;
							returnedIds.add(entry.outcome_id_normalized);
						}
					}
					// For IDs that were requested but not returned, mark them as attempted
					// (they might not exist or have no history, which is fine)
					outcomeIds.forEach(id => {
						if (!returnedIds.has(id)) {
							// Store empty object to indicate we tried but got no data
							priceHistoryCacheRef.current[id] = {};
						}
					});
					setPriceHistoryCacheVersion(v => v + 1);
					setIsPreloadingData(false); // Price history loaded for visible rows
					
					// Invalidate confidence cache for rows that now have price history
					// This ensures confidence recalculates with the new direction data
					if (dataRef.current && Array.isArray(dataRef.current)) {
						for (const row of dataRef.current) {
							const cacheKey = row.outcome_id_normalized || row.id;
							if (cacheKey && (returnedIds.has(row._linkOutcomeId) || returnedIds.has(row._sharpOutcomeId))) {
								confidenceCacheRef.current.delete(cacheKey);
							}
						}
					}
				} else {
					// If response is not an array, mark all as attempted with empty cache entries
					outcomeIds.forEach(id => {
						priceHistoryCacheRef.current[id] = {};
					});
					setPriceHistoryCacheVersion(v => v + 1);
				}
			} catch (e) {
				console.error('[Price History] Error fetching history for visible rows:', e);
				// Mark all as attempted even on error, so they don't stay in loading forever
				outcomeIds.forEach(id => {
					attemptedFetchRef.current.add(id);
					priceHistoryCacheRef.current[id] = {}; // Store empty to indicate attempted
				});
				setPriceHistoryCacheVersion(v => v + 1);
			}
		}, 150); // 150ms debounce to batch rows entering viewport together
	}, []);

	// Debounced batch fetch for related odds of visible rows
	const scheduleRelatedOddsFetch = useCallback(() => {
		if (relatedOddsFetchTimerRef.current) clearTimeout(relatedOddsFetchTimerRef.current);
		relatedOddsFetchTimerRef.current = setTimeout(async () => {
			const pending = new Map(pendingRelatedOddsFetchRef.current);
			pendingRelatedOddsFetchRef.current.clear();
			if (pending.size === 0) return;

			// Fetch related odds for all pending outcome IDs
			const fetchPromises = Array.from(pending.entries()).map(async ([outcomeId, { sport, options }]) => {
				try {
					const odds = await getRelatedOdds(outcomeId, sport, options);
					return { outcomeId, odds: odds || [] };
				} catch (e) {
					console.error(`[Related Odds] Error fetching for ${outcomeId}:`, e);
					return { outcomeId, odds: [] };
				}
			});

			const results = await Promise.all(fetchPromises);
			results.forEach(({ outcomeId, odds }) => {
				relatedOddsCacheRef.current.set(outcomeId, odds);
				attemptedRelatedOddsFetchRef.current.add(outcomeId);
			});

			setRelatedOddsCacheVersion(v => v + 1);
			
			// Invalidate confidence cache for rows that now have related odds
			if (dataRef.current && Array.isArray(dataRef.current)) {
				const returnedIds = new Set(results.map(r => r.outcomeId));
				for (const row of dataRef.current) {
					const cacheKey = row.outcome_id_normalized || row.id;
					if (cacheKey && returnedIds.has(row.outcome_id_normalized_no_book)) {
						confidenceCacheRef.current.delete(cacheKey);
					}
				}
			}
		}, 150); // 150ms debounce to batch rows entering viewport together
	}, []);

	// Setup IntersectionObserver for lazy price history and related odds loading
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				let needsPriceHistoryFetch = false;
				let needsRelatedOddsFetch = false;
				
				for (const entry of entries) {
					if (entry.isIntersecting) {
						const el = entry.target;
						const linkId = el.dataset.linkOutcomeId;
						const sharpId = el.dataset.sharpOutcomeId;
						const sport = el.dataset.sport;
						const outcomeIdNoBook = el.dataset.outcomeIdNoBook;
						const market = el.dataset.market || '';
						const points = el.dataset.points || '';
						const homeTeam = el.dataset.homeTeam || '';

						// Price history fetching
						if (linkId && linkId !== '' && !priceHistoryCacheRef.current[linkId] && !attemptedFetchRef.current.has(linkId)) {
							pendingFetchRef.current.set(linkId, sport);
							needsPriceHistoryFetch = true;
						}
						if (sharpId && sharpId !== '' && !priceHistoryCacheRef.current[sharpId] && !attemptedFetchRef.current.has(sharpId)) {
							pendingFetchRef.current.set(sharpId, sport);
							needsPriceHistoryFetch = true;
						}

						// Related odds fetching
						if (outcomeIdNoBook && outcomeIdNoBook !== '' && 
							!relatedOddsCacheRef.current.has(outcomeIdNoBook) && 
							!attemptedRelatedOddsFetchRef.current.has(outcomeIdNoBook)) {
							pendingRelatedOddsFetchRef.current.set(outcomeIdNoBook, {
								sport,
								options: { market, points, home_team: homeTeam }
							});
							needsRelatedOddsFetch = true;
						}
					}
				}
				
				if (needsPriceHistoryFetch) schedulePriceHistoryFetch();
				if (needsRelatedOddsFetch) scheduleRelatedOddsFetch();
			},
			{ threshold: 0, rootMargin: '200px 0px' } // Prefetch rows within 200px of viewport edge
		);

		rowObserverRef.current = observer;
		return () => observer.disconnect();
	}, [schedulePriceHistoryFetch, scheduleRelatedOddsFetch]);

	// Ref callback to register row elements with the IntersectionObserver
	const observeRowRef = useCallback((el) => {
		if (el && rowObserverRef.current) {
			rowObserverRef.current.observe(el);
		}
	}, []);

	// Helper to check if price history is loaded for a row
	const isPriceHistoryLoaded = useCallback((row) => {
		if (!row) return true; // No row means assume loaded
		const cache = priceHistoryCacheRef.current;
		const attempted = attemptedFetchRef.current;
		
		// If row has no outcome IDs, assume loaded (legacy data or no history needed)
		if (!row._linkOutcomeId && !row._sharpOutcomeId) return true;
		
		// If outcome IDs are empty strings, assume loaded (no history available)
		if (row._linkOutcomeId === '' && row._sharpOutcomeId === '') return true;
		
		// Check if we've attempted to fetch these IDs (even if they failed or returned no data)
		const linkAttempted = !row._linkOutcomeId || row._linkOutcomeId === '' || attempted.has(row._linkOutcomeId) || cache[row._linkOutcomeId];
		const sharpAttempted = !row._sharpOutcomeId || row._sharpOutcomeId === '' || attempted.has(row._sharpOutcomeId) || cache[row._sharpOutcomeId];
		
		// If both IDs have been attempted (or don't exist), consider it loaded
		return linkAttempted && sharpAttempted;
	}, []);

	// Keep data ref updated
	useEffect(() => {
		dataRef.current = data;
	}, [data]);

	// Invalidate confidence cache when price history loads
	// This ensures confidence recalculates with the new direction data
	useEffect(() => {
		if (priceHistoryCacheVersion > 0 && data && Array.isArray(data)) {
			// Clear confidence cache for all rows so they recalculate with new price history
			// The confidence calculation will check if directions have changed and recalculate accordingly
			const cacheKeysToInvalidate = new Set();
			for (const row of data) {
				const cacheKey = row.outcome_id_normalized || row.id;
				if (cacheKey) {
					cacheKeysToInvalidate.add(cacheKey);
				}
			}
			// Delete cache entries for rows that might have new price history
			cacheKeysToInvalidate.forEach(key => {
				const cacheEntry = confidenceCacheRef.current.get(key);
				if (cacheEntry) {
					// Only invalidate if directions were previously 0 (meaning they might have changed)
					// This prevents unnecessary recalculations
					if (!cacheEntry.hasGoodDirections) {
						confidenceCacheRef.current.delete(key);
					}
				}
			});
		}
	}, [priceHistoryCacheVersion, data]);

	// Invalidate confidence cache when related odds load
	// This ensures confidence recalculates with the new market average data
	useEffect(() => {
		if (relatedOddsCacheVersion > 0 && data && Array.isArray(data)) {
			// Clear confidence cache for rows that now have related odds
			for (const row of data) {
				const cacheKey = row.outcome_id_normalized || row.id;
				if (cacheKey && relatedOddsCacheRef.current.has(row.outcome_id_normalized_no_book)) {
					const cacheEntry = confidenceCacheRef.current.get(cacheKey);
					if (cacheEntry) {
						// Only invalidate if market average was previously bad (0) or wasn't using lazy-loaded odds
						// This ensures we recalculate when better data becomes available
						if (!cacheEntry.hasGoodMarketAverage || !cacheEntry.usedLazyRelatedOdds) {
							confidenceCacheRef.current.delete(cacheKey);
						}
					}
				}
			}
		}
	}, [relatedOddsCacheVersion, data]);

	// Clear price history and related odds caches when new data arrives
	useEffect(() => {
		if (data && Array.isArray(data) && data.length > 0) {
			// Clear caches since new scan data means new outcome IDs
			priceHistoryCacheRef.current = {};
			attemptedFetchRef.current.clear();
			setPriceHistoryCacheVersion(0);
			relatedOddsCacheRef.current.clear();
			attemptedRelatedOddsFetchRef.current.clear();
			setRelatedOddsCacheVersion(0);
			setIsPreloadingData(true); // Will be cleared once price history loads for visible rows
			
			// Clear confidence cache when new data arrives to ensure fresh calculations
			// Only clear entries that don't exist in the new data
			const newDataKeys = new Set(data.map(row => row.outcome_id_normalized || row.id).filter(Boolean));
			for (const key of confidenceCacheRef.current.keys()) {
				if (!newDataKeys.has(key)) {
					confidenceCacheRef.current.delete(key);
				}
			}
		}
	}, [data]);

	useEffect(() => {
		const getRelated = async () => {
			try {
				const relatedOdds = await getRelatedOdds(
					selectedRow.outcome_id_normalized_no_book,
					selectedRow.sport,
					{ market: selectedRow.market, points: selectedRow.points, home_team: selectedRow.home_team }
				);
				setRelatedOdds(relatedOdds);
			} catch (e) {
				console.error('[Related Odds]', e?.message || e);
				setRelatedOdds([]);
			}
		};

		let intervalId;
		let intervalId2;

		if (isAutoRefresh && selectedRow) {
			intervalId = setInterval(() => {
				getRelated();
			}, 8000); // 10 seconds
		} else if (selectedRow) {
			intervalId2 = setInterval(() => {
				getRelated();
			}, 30000);
		}

		// Cleanup on unmount or when isAutoRefresh changes
		return () => {
			if (intervalId) clearInterval(intervalId);
			if (intervalId2) clearInterval(intervalId2);
		};
	}, [isAutoRefresh, selectedRow, setRelatedOdds]);

	useEffect(() => {
		const getPlacedBets = async () => {
			try {
				const accessToken = window.localStorage.getItem('access_token');

				const bets = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/get_placed_bets?email=${user?.email}`, {
					headers: {
						Authorization: `Bearer ${accessToken}`,
					},
				});
				// console.log('bets: ', bets.data);
				setPlacedBets(bets.data?.placed_bets || []);
			} catch (error) {
				console.error('Error fetching placed bets:', error);
				setPlacedBets([]);
			}
		};
		if (!initialized && user?.email) {
			getPlacedBets();
			setInitialized(true);
		}
	}, [initialized, user?.email]);

	// Click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (selectedRow && dropdownRef.current && !dropdownRef.current.contains(event.target) && !tableContainerRef.current.contains(event.target)) {
				setSelectedRow();
				setSelectedRowIndex(null);
			}
		};

		if (selectedRow) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [selectedRow, setSelectedRow]);

	// Click outside to close popover
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (amountPopoverAnchor && popoverRef.current) {
				// Check if click is outside the popover and not on the checkbox/trigger area
				const isInsidePopover = popoverRef.current.contains(event.target);
				const isCheckboxArea = event.target.closest('[data-popover-trigger]');
				
				if (!isInsidePopover && !isCheckboxArea) {
					if (hoverTimeoutRef.current) {
						clearTimeout(hoverTimeoutRef.current);
					}
					setAmountPopoverAnchor(null);
					setAmountPopoverRowId(null);
				}
			}
		};

		if (amountPopoverAnchor) {
			// Use a small delay to avoid closing immediately when opening
			const timeoutId = setTimeout(() => {
				document.addEventListener('mousedown', handleClickOutside);
			}, 100);

			return () => {
				clearTimeout(timeoutId);
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}
	}, [amountPopoverAnchor]);

	// Measure dropdown height when it's rendered
	useEffect(() => {
		if (selectedRow && dropdownRef.current) {
			const height = dropdownRef.current.offsetHeight;
			setDropdownHeight(height);
		} else {
			setDropdownHeight(0);
		}
	}, [selectedRow, relatedOdds]);

	const [selectedRowIndex, setSelectedRowIndex] = useState(null);
	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
	const [dropdownHeight, setDropdownHeight] = useState(0);
	const tableContainerRef = useRef(null);
	const dropdownRef = useRef(null);
	
	// Memoize confidence scores per row to prevent flickering when relatedOdds changes
	const confidenceCacheRef = useRef(new Map());

	// Canonical sorted + filtered rows (no hard 150-row slice)
	const sortedFilteredRows = useMemo(() => {
		let rows = [];

		if (!data || !Array.isArray(data) || data.length === 0) {
			return rows;
		}

		if (data?.[1]?.outcome_id_normalized === '') {
			rows = data.slice();
		} else if (allBetsOpen === 'all') {
			rows = data
				?.slice()
				?.sort(getComparator(order, orderBy), [data, order, orderBy])
				.filter((item, index, self) => {
					// Use unique id field if available (includes points value), otherwise fall back to outcome_id_normalized + sportsbook + points
					if (item.id) {
						return index === self.findIndex((t) => t.id === item.id);
					}
					// Fallback for legacy data without id field - use sportsbook_dumb or sportsbook
					const sportsbook = item.sportsbook || item.sportsbook_dumb || '';
					const key = `${item.outcome_id_normalized || ''}_${sportsbook}_${item.points || ''}`;
					return index === self.findIndex((t) => {
						const otherSportsbook = t.sportsbook || t.sportsbook_dumb || '';
						const otherKey = `${t.outcome_id_normalized || ''}_${otherSportsbook}_${t.points || ''}`;
						return otherKey === key;
					});
				}) || [];
		} else if (allBetsOpen === 'placed') {
			rows = data
				?.slice()
				?.sort(getComparator(order, orderBy), [data, order, orderBy])
				.filter((item, index, self) => {
					if (item.id) {
						return index === self.findIndex((t) => t.id === item.id);
					}
					const sportsbook = item.sportsbook || item.sportsbook_dumb || '';
					const key = `${item.outcome_id_normalized || ''}_${sportsbook}_${item.points || ''}`;
					return index === self.findIndex((t) => {
						const otherSportsbook = t.sportsbook || t.sportsbook_dumb || '';
						const otherKey = `${t.outcome_id_normalized || ''}_${otherSportsbook}_${t.points || ''}`;
						return otherKey === key;
					});
				})
				.filter((row) => placedBets?.some((placedbet) => placedbet.id === row.outcome_id_normalized)) || [];
		} else if (allBetsOpen === 'unplaced') {
			rows = data
				?.slice()
				?.sort(getComparator(order, orderBy), [data, order, orderBy])
				.filter((item, index, self) => {
					if (item.id) {
						return index === self.findIndex((t) => t.id === item.id);
					}
					const sportsbook = item.sportsbook || item.sportsbook_dumb || '';
					const key = `${item.outcome_id_normalized || ''}_${sportsbook}_${item.points || ''}`;
					return index === self.findIndex((t) => {
						const otherSportsbook = t.sportsbook || t.sportsbook_dumb || '';
						const otherKey = `${t.outcome_id_normalized || ''}_${otherSportsbook}_${t.points || ''}`;
						return otherKey === key;
					});
				})
				.filter((row) => !placedBets?.some((placedbet) => placedbet.id === row.outcome_id_normalized)) || [];
		}

		// When using WagerWiz sharp: only show plays where sharp book coverage score > 15
		// (at least one higher-weighted book or 2+ low-weighted ones)
		// Skip this filter when freeRowLimit is set (free EV scanner data has sparse sharp_book info)
		const isWagerWizSharp = sharpFilter === 'WagerWiz' || sharpFilter?.includes?.('WagerWiz Mains') || sharpFilter?.includes?.('WagerWiz Props');
		if (isWagerWizSharp && rows.length > 0 && freeRowLimit == null) {
			rows = rows.filter((row) => {
				const coverage = calculateSharpBookCoverage(row.sharp_book, row.market);
				return coverage > 15;
			});
		}

		// 👇 Add the selectedRow back if it's not already there (and not filtered out by WagerWiz coverage)
		if (selectedRow && selectedRow.outcome_id_normalized && !rows.some((row) => row.outcome_id_normalized === selectedRow.outcome_id_normalized)) {
			const selectedCoverage = isWagerWizSharp ? calculateSharpBookCoverage(selectedRow.sharp_book, selectedRow.market) : 100;
			if (selectedCoverage > 15) {
				const insertIndex = Math.min(selectedRowIndex ?? rows.length, rows.length);
				rows = [...rows.slice(0, insertIndex), selectedRow, ...rows.slice(insertIndex)];
			}
		}

		return rows;
	}, [data, order, orderBy, allBetsOpen, placedBets, selectedRow, selectedRowIndex, sharpFilter, freeRowLimit]);

	// Augment sorted/filtered rows with lazy-loaded price history and related odds from the IntersectionObserver cache
	const augmentedRows = useMemo(() => {
		if (!sortedFilteredRows) return sortedFilteredRows;
		const priceHistoryCache = priceHistoryCacheRef.current;
		const relatedOddsCache = relatedOddsCacheRef.current;
		const hasPriceHistory = Object.keys(priceHistoryCache).length > 0;
		const hasRelatedOdds = relatedOddsCache.size > 0;

		if (!hasPriceHistory && !hasRelatedOdds) return sortedFilteredRows;

		return sortedFilteredRows.map(row => {
			let augmentedRow = { ...row };
			
			// Merge price history
			if (hasPriceHistory) {
				const linkHistory = priceHistoryCache[row._linkOutcomeId];
				const sharpHistory = priceHistoryCache[row._sharpOutcomeId];
				const hasLinkHistory = linkHistory && Object.keys(linkHistory).length > 0;
				const hasSharpHistory = sharpHistory && Object.keys(sharpHistory).length > 0;
				
				if (hasLinkHistory || hasSharpHistory) {
					augmentedRow = {
						...augmentedRow,
						link: hasLinkHistory ? { ...(augmentedRow.link || {}), ...linkHistory } : augmentedRow.link,
						sharpLink: hasSharpHistory ? { ...(augmentedRow.sharpLink || {}), ...sharpHistory } : augmentedRow.sharpLink,
					};
				}
			}
			
			// Add related odds to row (for confidence calculation)
			if (hasRelatedOdds && row.outcome_id_normalized_no_book && relatedOddsCache.has(row.outcome_id_normalized_no_book)) {
				augmentedRow._lazyRelatedOdds = relatedOddsCache.get(row.outcome_id_normalized_no_book);
			}
			
			return augmentedRow;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortedFilteredRows, priceHistoryCacheVersion, relatedOddsCacheVersion]);

	// Fine-tune scroll position after dropdown renders to account for its height
	useEffect(() => {
		if (selectedRow && relatedOdds && dropdownRef.current && selectedRowIndex !== null && augmentedRows.length > 0) {
			// Use setTimeout to ensure the dropdown is fully rendered
			const scrollTimeout = setTimeout(() => {
				if (!dropdownRef.current || !tableContainerRef.current) return;
				
				// Find the selected row element by index
				const tableRows = Array.from(tableContainerRef.current.querySelectorAll('.evtable-row'));
				const selectedRowElement = tableRows[selectedRowIndex];
				
				if (!selectedRowElement) return;
				
				// Get row position relative to viewport
				const rowRect = selectedRowElement.getBoundingClientRect();
				
				// Find scrollable parent
				let scrollableParent = tableContainerRef.current;
				let foundScrollable = false;
				
				while (scrollableParent && scrollableParent !== document.body && scrollableParent !== document.documentElement) {
					const style = window.getComputedStyle(scrollableParent);
					const overflowY = style.overflowY || style.overflow;
					const hasVerticalScroll = scrollableParent.scrollHeight > scrollableParent.clientHeight;
					
					if (hasVerticalScroll && (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')) {
						foundScrollable = true;
						break;
					}
					
					scrollableParent = scrollableParent.parentElement;
				}
				
				if (!foundScrollable) {
					scrollableParent = window;
				}
				
				// Calculate optimal scroll position: row should be at top (small offset), dropdown should be fully visible
				const targetRowTop = 10; // Small offset from top
				const currentRowTop = rowRect.top;
				
				// Get dropdown position to ensure it's fully visible
				const dropdownRect = dropdownRef.current.getBoundingClientRect();
				const viewportHeight = window.innerHeight;
				const dropdownBottom = dropdownRect.bottom;
				
				// Check if dropdown extends below viewport
				const dropdownOverflow = dropdownBottom - viewportHeight;
				
				// Calculate scroll adjustment
				// Priority: ensure dropdown is fully visible, then position row at top
				let scrollAdjustment = 0;
				
				if (dropdownOverflow > 0) {
					// Dropdown is cut off at bottom - scroll down to show it fully
					// Scroll down by the overflow amount plus some padding
					scrollAdjustment = -(dropdownOverflow + 20); // Negative means scroll down (increase scroll position)
				} else {
					// Dropdown is fully visible, position row at top
					scrollAdjustment = currentRowTop - targetRowTop;
				}
				
				// Only adjust if significantly off (more than 10px)
				if (Math.abs(scrollAdjustment) > 10) {
					if (scrollableParent === window) {
						const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
						window.scrollTo({
							top: Math.max(0, currentScrollY - scrollAdjustment),
							behavior: 'smooth'
						});
					} else {
						const currentScrollTop = scrollableParent.scrollTop;
						scrollableParent.scrollTo({
							top: Math.max(0, currentScrollTop - scrollAdjustment),
							behavior: 'smooth'
						});
					}
				}
			}, 300); // Wait for dropdown to fully render
			
			return () => clearTimeout(scrollTimeout);
		}
	}, [selectedRow, relatedOdds, selectedRowIndex, augmentedRows.length]);

	// Auto-scroll to show expanded dropdown when last row is selected
	useEffect(() => {
		if (selectedRow && relatedOdds && dropdownRef.current && selectedRowIndex !== null && augmentedRows.length > 0) {
			// Check if the selected row is the last row
			const isLastRow = selectedRowIndex === augmentedRows.length - 1;
			
			if (isLastRow) {
				// Use setTimeout to ensure the dropdown is fully rendered
				const scrollTimeout = setTimeout(() => {
					if (!dropdownRef.current || !tableContainerRef.current) return;
					
					// Get dropdown position relative to viewport
					const dropdownRect = dropdownRef.current.getBoundingClientRect();
					const dropdownBottom = dropdownRect.bottom;
					const dropdownHeight = dropdownRect.height;
					
					// Find all potential scrollable parents
					let element = tableContainerRef.current;
					const scrollableParents = [];
					
					// Traverse up the DOM to find all scrollable parents
					while (element && element !== document.body && element !== document.documentElement) {
						const style = window.getComputedStyle(element);
						const overflowY = style.overflowY || style.overflow;
						const hasVerticalScroll = element.scrollHeight > element.clientHeight;
						
						// Check if this element is scrollable
						if (hasVerticalScroll) {
							scrollableParents.push({
								element: element,
								overflowY: overflowY,
								scrollTop: element.scrollTop,
								scrollHeight: element.scrollHeight,
								clientHeight: element.clientHeight
							});
						}
						
						element = element.parentElement;
					}
					
					// If we found scrollable parents, scroll the closest one to show the dropdown
					if (scrollableParents.length > 0) {
						// Use the first (closest) scrollable parent
						const scrollableElement = scrollableParents[0].element;
						
						// Always scroll to the bottom to ensure the dropdown is fully visible
						// This is especially important for the last row
						const maxScrollTop = scrollableElement.scrollHeight - scrollableElement.clientHeight;
						
						// Scroll to bottom with smooth behavior
						scrollableElement.scrollTo({
							top: maxScrollTop,
							behavior: 'smooth'
						});
					} else {
						// No scrollable parent found, use scrollIntoView as fallback
						dropdownRef.current.scrollIntoView({
							behavior: 'smooth',
							block: 'end',
							inline: 'nearest'
						});
					}
				}, 200); // Increased delay to ensure dropdown is fully rendered
				
				return () => clearTimeout(scrollTimeout);
			}
		}
	}, [selectedRow, relatedOdds, selectedRowIndex, augmentedRows.length]);

	if (!isMobile) {
		return (
			<div ref={tableContainerRef} className="table-container" style={{ 
				overflowX: 'hidden', 
				overflowY: 'visible',
				borderRadius: '16px',
				background: 'linear-gradient(135deg, #17191d 0%, #1a1d23 100%)',
				padding: '0',
				margin: '0',
				position: 'relative',
				width: '100%',
				boxSizing: 'border-box'
			}}>
				<Table
					id="ev-screen-table"
					sx={{
						'& .MuiTableCell-root': {
							fontFamily: 'Mulish, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
							fontSize: '0.875rem',
							lineHeight: 1.5,
						},
						backgroundColor: 'transparent',
						tableLayout: 'fixed',
						width: '100%',
						'& .MuiTableContainer-root': {
							borderRadius: '16px',
						}
					}}>
				<UpgradeModal isOpen={isUpgradeModalOpen} setIsOpen={setIsUpgradeModalOpen} />
				<TableHead
					sx={{
						'& .MuiTableRow-root:first-of-type .MuiTableCell-root:first-of-type': {
							borderTopLeftRadius: '16px',
						},
						'& .MuiTableRow-root:first-of-type .MuiTableCell-root:last-of-type': {
							borderTopRightRadius: '16px',
						},
						'& .MuiTableCell-root': {
							borderBottom: 'none',
							borderRight: 'none',
							borderLeft: 'none',
							background: 'transparent',
						}
					}}
					className="tableHead">
					<TableRow sx={{ 
						height: 80,
						'&:hover': {
							backgroundColor: 'rgba(255, 255, 255, 0.05)',
						}
					}}>
													<TableCell
								sx={{ width: '7%', minWidth: '60px', textAlign: 'left' }}
								onClick={() => {
									setOrderBy('league');
									setOrder(order === 'asc' ? 'desc' : 'asc');
								}}
								className="tableHeadCell cursor-pointer">
								LEAGUE
							</TableCell>
													<TableCell
								sx={{ width: '9%', minWidth: '90px', textAlign: 'left' }}
								onClick={() => {
									setOrderBy('market');
									setOrder(order === 'asc' ? 'desc' : 'asc');
								}}
								className="tableHeadCell cursor-pointer">
								MARKET
							</TableCell>
													<TableCell
								sx={{ width: '11%', minWidth: '110px', textAlign: 'left' }}
								onClick={() => {
									setOrderBy('game');
									setOrder(order === 'asc' ? 'desc' : 'asc');
								}}
								className="tableHeadCell cursor-pointer">
								GAME
							</TableCell>
						<TableCell
							sx={{ width: '9%', minWidth: '90px', textAlign: 'left' }}
							onClick={() => {
								setOrderBy('bet_name');
								setOrder(order === 'asc' ? 'desc' : 'asc');
							}}
							className="tableHeadCell cursor-pointer">
							BET
						</TableCell>
													<TableCell
								sx={{ width: '8%', minWidth: '80px', textAlign: 'left' }}
								onClick={() => {
									setOrderBy('sportsbook_dumb');
									setOrder(order === 'asc' ? 'desc' : 'asc');
								}}
								className="tableHeadCell cursor-pointer">
								BOOK
							</TableCell>
													<TableCell
								sx={{ width: '9%', minWidth: '90px', textAlign: 'left' }}
								onClick={() => {
									setOrderBy('bet_price');
									setOrder(order === 'asc' ? 'desc' : 'asc');
								}}
								className="tableHeadCell cursor-pointer">
								PRICE
							</TableCell>
													<TableCell
								sx={{ width: '9%', minWidth: '90px', textAlign: 'left' }}
								onClick={() => {
									setOrderBy('sharp_book');
									setOrder(order === 'asc' ? 'desc' : 'asc');
								}}
								className="tableHeadCell cursor-pointer">
								SHARP
							</TableCell>
													<TableCell
								sx={{ width: '11%', minWidth: '110px', textAlign: 'left' }}
								onClick={() => {
									setOrderBy('devigged1');
									setOrder(order === 'asc' ? 'desc' : 'asc');
								}}
								className="tableHeadCell cursor-pointer">
								FAIR VALUE
							</TableCell>
													<TableCell
								sx={{ width: '8%', minWidth: '60px', textAlign: 'left' }}
								onClick={() => {
									setOrderBy('ev');
									setOrder(order === 'asc' ? 'desc' : 'asc');
								}}
								className="tableHeadCell cursor-pointer">
								EV%
							</TableCell>
													<TableCell
								sx={{ width: '7%', minWidth: '60px', textAlign: 'left' }}
								onClick={() => {
									setOrderBy('quarter_kelly');
									setOrder(order === 'asc' ? 'desc' : 'asc');
								}}
								className="tableHeadCell cursor-pointer">
								BET SIZE
							</TableCell>
													{process.env.REACT_APP_CONFIDENCE !== 'false' && (
								<TableCell
									sx={{
										width: '10%',
										minWidth: '72px',
										textAlign: 'center',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
									onClick={() => {
										setOrderBy('confidence');
										setOrder(order === 'asc' ? 'desc' : 'asc');
									}}
									className="tableHeadCell cursor-pointer confidence-header-cell">
									<Tooltip title="Confidence score is in beta. Always verify your own plays and exercise caution." placement="top" arrow>
										<span>Confidence</span>
									</Tooltip>
								</TableCell>
							)}
						<TableCell sx={{ width: '10%', minWidth: '120px' }} className="tableHeadCell cursor-pointer">BET PLACED?</TableCell>
					</TableRow>
				</TableHead>
				<TableBody className={`tableBody ${isLoading ? 'loading' : ''}`}>
					{isLoading ? (
						// Show loading skeleton rows
						Array.from({ length: 5 }).map((_, index) => (
							<TableRow key={`loading-${index}`} className="evtable-row">
								<TableCell style={{ color: '#f0f2f6', width: '7%', minWidth: '60px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', width: '9%', minWidth: '90px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', width: '11%', minWidth: '110px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', textAlign: 'center', width: '9%', minWidth: '90px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', width: '13%', minWidth: '120px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', width: '9%', minWidth: '90px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', width: '13%', minWidth: '120px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', width: '11%', minWidth: '110px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', width: '8%', minWidth: '60px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', width: '7%', minWidth: '60px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', width: '10%', minWidth: '72px', textAlign: 'center' }}>
									<div style={{ 
										height: '12px', 
										width: '12px',
										backgroundColor: '#363844', 
										borderRadius: '50%',
										animation: 'pulse 1.5s ease-in-out infinite',
										margin: '0 auto'
									}} />
								</TableCell>
								<TableCell style={{ color: '#f0f2f6', width: '10%', minWidth: '120px' }}>
									<div style={{ 
										height: '20px', 
										backgroundColor: '#363844', 
										borderRadius: '4px',
										animation: 'pulse 1.5s ease-in-out infinite'
									}} />
								</TableCell>
							</TableRow>
						))
					) : (
						augmentedRows?.slice(0, 150).map((dataRow, index) => {
							let betName = '';
							let betPrice = '';
							let sharpPrice = '';
							let sharpPriceOver = '';
							let sharpPriceUnder = '';
							let quarter_kelly = 0;
							let ev_value = 0;
							let link = '';
							let sharpLink = '';
							let bet_price_direction_dumb = 0;
							let bet_price_direction_sharp = 0;
							let limit;
							let isFairValueOver = false;
							
							// First, extract sharp prices for confidence calculation (before they're formatted)
							let sharpPriceOverRaw = '';
							let sharpPriceUnderRaw = '';
							
							// Calculate market average and bet odds for confidence calculation
							let marketAverage = 0;
							let betOdds = 0;
							let fairValue = null;
							let sharpOppositeSideOdds = null; // Pre-devigged sharp odds for OPPOSITE side (for arb calc)
							
							// Determine bet odds and fair value - extract raw sharp prices first
							if (dataRow.market === 'Point Spread' || dataRow.market === 'Asian Handicap') {
								if (dataRow.ev_spread === dataRow.ev_over_positive) {
									betOdds = dataRow.bet_price_over_positive_dumb;
									sharpPriceOverRaw = dataRow.bet_price_over_positive_sharp;
									sharpPriceUnderRaw = dataRow.bet_price_under_negative_sharp;
									let deviggedOdds = devigOddsProbit(Number(sharpPriceOverRaw), Number(sharpPriceUnderRaw)).overOdds;
									// Ensure the fair value sign matches the bet price sign
									const betPriceSign = betOdds < 0 ? -1 : 1;
									const deviggedSign = deviggedOdds < 0 ? -1 : 1;
									if (betPriceSign !== deviggedSign) {
										deviggedOdds = -deviggedOdds;
									}
									fairValue = deviggedOdds;
									// For arb: if betting over_positive, opposite is under_negative
									sharpOppositeSideOdds = Number(sharpPriceUnderRaw);
								} else if (dataRow.ev_spread === dataRow.ev_under_positive) {
									betOdds = dataRow.bet_price_under_positive_dumb;
									sharpPriceOverRaw = dataRow.bet_price_under_positive_sharp;
									sharpPriceUnderRaw = dataRow.bet_price_over_negative_sharp;
									let deviggedOdds = devigOddsProbit(Number(sharpPriceOverRaw), Number(sharpPriceUnderRaw)).underOdds;
									const betPriceSign = betOdds < 0 ? -1 : 1;
									const deviggedSign = deviggedOdds < 0 ? -1 : 1;
									if (betPriceSign !== deviggedSign) {
										deviggedOdds = -deviggedOdds;
									}
									fairValue = deviggedOdds;
									// For arb: if betting under_positive, opposite is over_negative
									sharpOppositeSideOdds = Number(sharpPriceUnderRaw);
								} else if (dataRow.ev_spread === dataRow.ev_over_negative) {
									betOdds = dataRow.bet_price_over_negative_dumb;
									sharpPriceOverRaw = dataRow.bet_price_over_negative_sharp;
									sharpPriceUnderRaw = dataRow.bet_price_under_positive_sharp;
									let deviggedOdds = devigOddsProbit(Number(sharpPriceOverRaw), Number(sharpPriceUnderRaw)).overOdds;
									const betPriceSign = betOdds < 0 ? -1 : 1;
									const deviggedSign = deviggedOdds < 0 ? -1 : 1;
									if (betPriceSign !== deviggedSign) {
										deviggedOdds = -deviggedOdds;
									}
									fairValue = deviggedOdds;
									// For arb: if betting over_negative, opposite is under_positive
									sharpOppositeSideOdds = Number(sharpPriceUnderRaw);
								} else if (dataRow.ev_spread === dataRow.ev_under_negative) {
									betOdds = dataRow.bet_price_under_negative_dumb;
									sharpPriceOverRaw = dataRow.bet_price_under_negative_sharp;
									sharpPriceUnderRaw = dataRow.bet_price_over_positive_sharp;
									let deviggedOdds = devigOddsProbit(Number(sharpPriceOverRaw), Number(sharpPriceUnderRaw)).underOdds;
									const betPriceSign = betOdds < 0 ? -1 : 1;
									const deviggedSign = deviggedOdds < 0 ? -1 : 1;
									if (betPriceSign !== deviggedSign) {
										deviggedOdds = -deviggedOdds;
									}
									fairValue = deviggedOdds;
									// For arb: if betting under_negative, opposite is over_positive
									sharpOppositeSideOdds = Number(sharpPriceUnderRaw);
								}
							} else if (dataRow.market === 'Moneyline') {
								if (dataRow.ev_over > dataRow.ev_under) {
									betOdds = dataRow.bet_price_over_dumb;
									sharpPriceOverRaw = dataRow.bet_price_over_sharp;
									sharpPriceUnderRaw = dataRow.bet_price_under_sharp;
									fairValue = devigOddsProbit(Number(sharpPriceOverRaw), Number(sharpPriceUnderRaw)).overOdds;
									// For arb: if betting OVER, opposite is UNDER
									sharpOppositeSideOdds = Number(sharpPriceUnderRaw);
								} else {
									betOdds = dataRow.bet_price_under_dumb;
									sharpPriceOverRaw = dataRow.bet_price_over_sharp;
									sharpPriceUnderRaw = dataRow.bet_price_under_sharp;
									fairValue = devigOddsProbit(Number(sharpPriceOverRaw), Number(sharpPriceUnderRaw)).underOdds;
									// For arb: if betting UNDER, opposite is OVER
									sharpOppositeSideOdds = Number(sharpPriceOverRaw);
								}
							} else {
								if (dataRow.ev_over > dataRow.ev_under) {
									betOdds = dataRow.bet_price_over_dumb;
									sharpPriceOverRaw = dataRow.bet_price_over_sharp;
									sharpPriceUnderRaw = dataRow.bet_price_under_sharp;
									fairValue = devigOddsProbit(Number(sharpPriceOverRaw), Number(sharpPriceUnderRaw)).overOdds;
									// For arb calc: if betting OVER, opposite side is UNDER
									sharpOppositeSideOdds = Number(sharpPriceUnderRaw);
								} else {
									betOdds = dataRow.bet_price_under_dumb;
									sharpPriceOverRaw = dataRow.bet_price_over_sharp;
									sharpPriceUnderRaw = dataRow.bet_price_under_sharp;
									fairValue = devigOddsProbit(Number(sharpPriceOverRaw), Number(sharpPriceUnderRaw)).underOdds;
									// For arb calc: if betting UNDER, opposite side is OVER
									sharpOppositeSideOdds = Number(sharpPriceOverRaw);
								}
							}
							
							// Calculate line movement directions BEFORE confidence calculation
							// This uses the same logic as the later display code
							const isOverBetForDirection = dataRow.ev_over > dataRow.ev_under;
							if (dataRow.market === 'Moneyline') {
								if (isOverBetForDirection) {
									bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_over_history);
									bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_over_history);
								} else {
									bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_under_history);
									bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_under_history);
								}
							} else if (dataRow.market === 'Point Spread' || dataRow.market === 'Asian Handicap') {
								// Point Spread uses positive/negative variants
								if (dataRow.ev_spread === dataRow.ev_over_positive) {
									bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_history_over_positive);
									bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_history_over_positive);
								} else if (dataRow.ev_spread === dataRow.ev_under_positive) {
									bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_history_under_positive);
									bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_history_under_positive);
								} else if (dataRow.ev_spread === dataRow.ev_over_negative) {
									bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_history_over_negative);
									bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_history_over_negative);
								} else if (dataRow.ev_spread === dataRow.ev_under_negative) {
									bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_history_under_negative);
									bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_history_under_negative);
								}
							} else {
								// Player props and other markets
								if (isOverBetForDirection) {
									bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_over_history);
									bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_over_history);
								} else {
									bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_under_history);
									bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_under_history);
								}
							}
							
							// Check if price history is still loading for this row
							const isPriceHistoryLoadingForRow = !isPriceHistoryLoaded(dataRow);
							
							// Determine overSide/underSide for market average calculation
							// Must match exactly what RelatedOddsDropdown receives as props
							let overSideForAvg = 'bet_price_over';
							let underSideForAvg = 'bet_price_under';
							let isSpreadForAvg = false;
							
							if (dataRow.market === 'Point Spread' || dataRow.market === 'Asian Handicap') {
								isSpreadForAvg = true;
								if (dataRow.ev_spread === dataRow.ev_over_positive) {
									overSideForAvg = 'bet_price_over_positive';
									underSideForAvg = 'bet_price_under_negative';
								} else if (dataRow.ev_spread === dataRow.ev_under_positive) {
									overSideForAvg = 'bet_price_over_negative';
									underSideForAvg = 'bet_price_under_positive';
								} else if (dataRow.ev_spread === dataRow.ev_over_negative) {
									overSideForAvg = 'bet_price_over_negative';
									underSideForAvg = 'bet_price_under_positive';
								} else if (dataRow.ev_spread === dataRow.ev_under_negative) {
									overSideForAvg = 'bet_price_over_positive';
									underSideForAvg = 'bet_price_under_negative';
								}
							}
							
							// Determine if this is an "over" or "under" bet for picking the right average column
							let isOverBetForAvg;
							if (dataRow.market === 'Point Spread' || dataRow.market === 'Asian Handicap') {
								isOverBetForAvg = dataRow.ev_spread === dataRow.ev_over_positive || 
											dataRow.ev_spread === dataRow.ev_over_negative;
							} else {
								isOverBetForAvg = dataRow.ev_over > dataRow.ev_under;
							}
							
							// Try to get relatedOdds from lazy-loaded cache, selected row, or legacy preloaded data
							let rowRelatedOdds = null;
							if (relatedOdds && relatedOdds.length > 0 && selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized) {
								// Use relatedOdds for selected row (most accurate and up-to-date)
								rowRelatedOdds = relatedOdds;
							} else if (dataRow._lazyRelatedOdds && Array.isArray(dataRow._lazyRelatedOdds) && dataRow._lazyRelatedOdds.length > 0) {
								// Use lazy-loaded related odds from viewport-based fetching
								rowRelatedOdds = dataRow._lazyRelatedOdds;
							} else if (preloadedRelatedOdds && preloadedRelatedOdds.has(dataRow.outcome_id_normalized_no_book)) {
								// Fallback to legacy preloadedRelatedOdds for backward compatibility
								rowRelatedOdds = preloadedRelatedOdds.get(dataRow.outcome_id_normalized_no_book);
							}

							// Calculate market average using the EXACT same logic as RelatedOddsDropdown
							if (rowRelatedOdds && rowRelatedOdds.length > 0) {
								// pickPrice: matches RelatedOddsDropdown.js exactly
								const pickPrice = (row, primaryKey, isSpread) => {
									const val = row?.[primaryKey];
									if (val != null) return val;
									if (!isSpread) return null;
									if (primaryKey?.startsWith('bet_price_over')) {
										return row?.bet_price_over_positive ?? row?.bet_price_over_negative ?? row?.bet_price_over ?? null;
									}
									if (primaryKey?.startsWith('bet_price_under')) {
										return row?.bet_price_under_positive ?? row?.bet_price_under_negative ?? row?.bet_price_under ?? null;
									}
									return null;
								};
								
								// americanToDecimal: matches RelatedOddsDropdown.js exactly (with .toFixed(2))
								const americanToDecimal = (oddsStr) => {
									const odds = parseFloat(oddsStr);
									if (isNaN(odds)) return null;
									if (odds > 0) return (odds / 100 + 1).toFixed(2);
									return (100 / Math.abs(odds) + 1).toFixed(2);
								};
								
								// decimalToAmerican: matches RelatedOddsDropdown.js exactly
								const decimalToAmerican = (decimal) => {
									if (decimal == null) return null;
									if (decimal >= 2.0) return Math.round((decimal - 1) * 100);
									if (decimal < 2.0 && decimal > 1) return Math.round(-100 / (decimal - 1));
									return null;
								};
								
								// Use the correct price field based on bet side
								const avgPriceField = isOverBetForAvg ? overSideForAvg : underSideForAvg;
								
								const decimalOdds = rowRelatedOdds
									.filter((e) => pickPrice(e, avgPriceField, isSpreadForAvg) != null)
									.map((e) => {
										try {
											const americanOdds = pickPrice(e, avgPriceField, isSpreadForAvg);
											if (americanOdds == null) return null;
											return parseFloat(americanToDecimal(americanOdds));
										} catch (err) {
											return null;
										}
									})
									.filter((d) => d != null);
								
								if (decimalOdds.length > 0) {
									const avgDecimal = decimalOdds.reduce((total, next) => total + next, 0) / decimalOdds.length;
									const result = decimalToAmerican(avgDecimal);
									if (result != null) {
										marketAverage = result;
									}
								}
							}
							// If no relatedOdds available or calculation fails, marketAverage stays at 0 → excluded from score

							// Calculate confidence with breakdown
							// Don't show confidence until price history is loaded (line movement is verified)
							// This ensures the confidence score is accurate with real line movement data
							
							// Use cached confidence score to prevent flickering when relatedOdds changes for other rows
							// Create a cache key based on the row's unique identifier and relevant data
							const cacheKey = dataRow.outcome_id_normalized || dataRow.id || `row-${index}`;
							const cacheEntry = confidenceCacheRef.current.get(cacheKey);
							
							// Determine if we're using lazy-loaded related odds
							const isUsingLazyRelatedOdds = dataRow._lazyRelatedOdds && Array.isArray(dataRow._lazyRelatedOdds) && dataRow._lazyRelatedOdds.length > 0;
							
							// Determine if we're using legacy preloaded data for this row (only if not using relatedOdds or lazy-loaded)
							const isUsingPreloadedData = !(relatedOdds && relatedOdds.length > 0 && selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized) &&
								!isUsingLazyRelatedOdds &&
								preloadedRelatedOdds && preloadedRelatedOdds.has(dataRow.outcome_id_normalized_no_book);
							
							// Determine if we're using relatedOdds for the selected row (more accurate than fallback)
							const isUsingRelatedOdds = relatedOdds && relatedOdds.length > 0 && selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized;
							
							// Determine if we have "good" data for confidence calculation
							// Track each data source separately so we recalculate when ANY improves
							const hasGoodMarketAverage = marketAverage !== 0;
							const hasGoodDirections = bet_price_direction_sharp !== 0 || bet_price_direction_dumb !== 0;
							
							// Recalculate if:
							// 1. No cache entry yet
							// 2. Market average improved (was bad, now good)
							// 3. Directions improved (were both 0, now at least one is non-zero)
							// 4. Lazy-loaded related odds just became available (wasn't using lazy, now using lazy)
							const shouldRecalculate = !cacheEntry || 
								!cacheEntry.confidenceData || 
								(!cacheEntry.hasGoodMarketAverage && hasGoodMarketAverage) ||
								(!cacheEntry.hasGoodDirections && hasGoodDirections) ||
								(!cacheEntry.usedLazyRelatedOdds && isUsingLazyRelatedOdds);
							
							let confidenceData;
							// Don't calculate confidence until price history is loaded (line movement verified)
							if (isPriceHistoryLoadingForRow) {
								confidenceData = { confidence: null, breakdown: null };
							} else if (shouldRecalculate) {
								// Calculate new confidence score
								// Pass pre-calculated directions and sharp opposite side odds for accurate scoring
								confidenceData = calculateConfidenceWithBreakdown(
									dataRow, 
									marketAverage, 
									sharpFilter, 
									fairValue, 
									betOdds,
									null, // availableBooks
									{
										sharpDirection: bet_price_direction_sharp,
										dumbDirection: bet_price_direction_dumb,
										sharpOppositeSideOdds: sharpOppositeSideOdds
									}
								);
								// Cache the result - track each data source separately
								confidenceCacheRef.current.set(cacheKey, {
									confidenceData,
									marketAverage,
									fairValue,
									betOdds,
									sharpFilter,
									usedPreloadedData: isUsingPreloadedData,
									usedRelatedOdds: isUsingRelatedOdds,
									usedLazyRelatedOdds: isUsingLazyRelatedOdds,
									hasGoodMarketAverage: hasGoodMarketAverage,
									hasGoodDirections: hasGoodDirections
								});
							} else {
								// Use cached confidence score
								confidenceData = cacheEntry.confidenceData;
							}

							if (dataRow.limit_sharp) {
								try {
									if (Array.isArray(dataRow.limit_sharp)) {
										for (const l of dataRow.limit_sharp) {
											if (l?.max) {
												limit = l.max;
											}
										}
									} else {
										limit = dataRow.limit_sharp?.max;
									}
								} catch (e) {
									console.log('e: ', e);
								}
							}

							let overSide = 'bet_price_over';
							let underSide = 'bet_price_under';

							const percentOfTotalBankroll = isNaN(placedBets?.find((placedbet) => placedbet.id === dataRow.outcome_id_normalized)?.value / bankroll)
								? 0
								: placedBets?.find((placedbet) => placedbet.id === dataRow.outcome_id_normalized)?.value / bankroll;

							// Handle cases where outcome_id_normalized might be undefined
							const outcomeIdSplit = dataRow.outcome_id_normalized ? dataRow.outcome_id_normalized.split(':') : [];
							let isSpread = false;

							if (dataRow.market === 'Point Spread' || dataRow.market === 'Asian Handicap') {
								if (dataRow.ev_spread === dataRow.ev_over_positive) {
									ev_value = dataRow.ev_over_positive * 100;
									betName = `${dataRow.game?.home_team_display} +${dataRow.points || ''}`;
									betPrice = formatPrice(dataRow.bet_price_over_positive_dumb);
									sharpPrice = `${formatPrice(dataRow.bet_price_over_positive_sharp) || ''} / ${formatPrice(dataRow.bet_price_under_negative_sharp) || ''}`;
									sharpPriceOver = formatPrice(dataRow.bet_price_over_positive_sharp);
									sharpPriceUnder = formatPrice(dataRow.bet_price_under_negative_sharp);
									quarter_kelly = dataRow.quarter_kelly_over_positive;
									// link = dataRow.link?.deep_link_over_positive?.desktop?.replace(/<STATE>./g, '');
									// link = dataRow.link?.deep_link?.desktop?.replace(/<STATE>./g, '');
									link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);

									sharpLink = formatSharpDeepLink(dataRow.sharpLink);

									overSide = 'bet_price_over_positive';
									underSide = 'bet_price_under_negative';
									isSpread = true;
									isFairValueOver = true;
								} else if (dataRow.ev_spread === dataRow.ev_under_positive) {
									ev_value = dataRow.ev_under_positive * 100;
									betName = `${dataRow.game?.away_team_display} +${dataRow.points || ''}`;
									betPrice = formatPrice(dataRow.bet_price_under_positive_dumb);
									sharpPrice = `${formatPrice(dataRow.bet_price_under_positive_sharp) || ''} / ${formatPrice(dataRow.bet_price_over_negative_sharp) || ''}`;
									sharpPriceOver = formatPrice(dataRow.bet_price_under_positive_sharp);
									sharpPriceUnder = formatPrice(dataRow.bet_price_over_negative_sharp);
									quarter_kelly = dataRow.quarter_kelly_under_positive;
									link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
									sharpLink = formatSharpDeepLink(dataRow.sharpLink);

									overSide = 'bet_price_over_negative';
									underSide = 'bet_price_under_positive';
									isSpread = true;
									isFairValueOver = false;
								} else if (dataRow.ev_spread === dataRow.ev_over_negative) {
									ev_value = dataRow.ev_over_negative * 100;
									betName = `${dataRow.game?.home_team_display} -${dataRow.points || ''}`;
									betPrice = formatPrice(dataRow.bet_price_over_negative_dumb);
									sharpPrice = `${formatPrice(dataRow.bet_price_over_negative_sharp) || ''} / ${formatPrice(dataRow.bet_price_under_positive_sharp) || ''}`;
									sharpPriceOver = formatPrice(dataRow.bet_price_over_negative_sharp);
									sharpPriceUnder = formatPrice(dataRow.bet_price_under_positive_sharp);
									quarter_kelly = dataRow.quarter_kelly_over_negative;
									// link = dataRow.link?.deep_link_over?.desktop?.replace(/<STATE>./g, '');

									link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
									sharpLink = formatSharpDeepLink(dataRow.sharpLink);

									overSide = 'bet_price_over_negative';
									underSide = 'bet_price_under_positive';
									isSpread = true;
									isFairValueOver = true;
								} else if (dataRow.ev_spread === dataRow.ev_under_negative) {
									ev_value = dataRow.ev_under_negative * 100;
									betName = `${dataRow.game?.away_team_display} -${dataRow.points || ''}`;
									betPrice = formatPrice(dataRow.bet_price_under_negative_dumb);
									sharpPrice = `${formatPrice(dataRow.bet_price_under_negative_sharp) || ''} / ${formatPrice(dataRow.bet_price_over_positive_sharp) || ''}`;
									sharpPriceOver = formatPrice(dataRow.bet_price_over_positive_sharp);
									sharpPriceUnder = formatPrice(dataRow.bet_price_under_negative_sharp);
									quarter_kelly = dataRow.quarter_kelly_under_negative;
									// link = dataRow.link?.deep_link_under?.desktop?.replace(/<STATE>./g, '');

									link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
									sharpLink = formatSharpDeepLink(dataRow.sharpLink);

									overSide = 'bet_price_over_positive';
									underSide = 'bet_price_under_negative';
									isSpread = true;
									isFairValueOver = false;
								}
							} else if (dataRow.market === 'Moneyline') {
								if (dataRow.ev_over > dataRow.ev_under) {
									betName = dataRow.game?.home_team_display;
									betPrice = formatPrice(dataRow.bet_price_over_dumb);
									sharpPrice = `${formatPrice(dataRow.bet_price_over_sharp)} / ${formatPrice(dataRow.bet_price_under_sharp)}`;
									sharpPriceOver = formatPrice(dataRow.bet_price_over_sharp);
									sharpPriceUnder = formatPrice(dataRow.bet_price_under_sharp);
									quarter_kelly = dataRow.quarter_kelly_over;
									ev_value = dataRow.ev_over * 100;
									link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
									sharpLink = formatSharpDeepLink(dataRow.sharpLink);
									bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_over_history);
									bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_over_history);
									isFairValueOver = true;
								} else {
									betName = dataRow.game?.away_team_display;
									betPrice = formatPrice(dataRow.bet_price_under_dumb);
									sharpPrice = `${formatPrice(dataRow.bet_price_under_sharp)} / ${formatPrice(dataRow.bet_price_over_sharp)}`;
									sharpPriceOver = formatPrice(dataRow.bet_price_over_sharp);
									sharpPriceUnder = formatPrice(dataRow.bet_price_under_sharp);
									quarter_kelly = dataRow.quarter_kelly_under;
									ev_value = dataRow.ev_under * 100;
									link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
									sharpLink = formatSharpDeepLink(dataRow.sharpLink);
									bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_under_history);
									bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_under_history);
									isFairValueOver = false;
								}
							} else if (dataRow.ev_over > dataRow.ev_under) {
								// console.log('dataRow: ', dataRow);
								ev_value = dataRow.ev_over * 100;
								betName = `${outcomeIdSplit[2] || ''} over ${outcomeIdSplit[3] || ''}`;
								betPrice = formatPrice(dataRow.bet_price_over_dumb);
								sharpPrice = `${formatPrice(dataRow.bet_price_over_sharp)} / ${formatPrice(dataRow.bet_price_under_sharp)}`;
								sharpPriceOver = formatPrice(dataRow.bet_price_over_sharp);
								sharpPriceUnder = formatPrice(dataRow.bet_price_under_sharp);
								quarter_kelly = dataRow.quarter_kelly_over;
								link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
								// console.log('link: ', link, dataRow);

								sharpLink = formatSharpDeepLink(dataRow.sharpLink);
								bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_over_history);
								bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_over_history);
								isFairValueOver = true
							} else {
								betName = `${outcomeIdSplit[2] || ''} under ${outcomeIdSplit[3] || ''}`;
								betPrice = formatPrice(dataRow.bet_price_under_dumb);
								sharpPrice = `${formatPrice(dataRow.bet_price_under_sharp)} / ${formatPrice(dataRow.bet_price_over_sharp)}`;
								sharpPriceOver = formatPrice(dataRow.bet_price_over_sharp);
								sharpPriceUnder = formatPrice(dataRow.bet_price_under_sharp);
								quarter_kelly = dataRow.quarter_kelly_under;
								ev_value = dataRow.ev_under * 100;
								link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
								sharpLink = formatSharpDeepLink(dataRow.sharpLink);
								bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_under_history);
								bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_under_history);
								isFairValueOver = false;
							}
							
							if (dataRow.outcome_id_normalized === '') {
								betName = '';
							}
							// console.log('betName: ', betName, outcomeIdSplit);

							let sharpBook = dataRow.sharp_book;

							if (dataRow.sharp_filter?.includes('WagerWiz Mains') || dataRow.sharp_filter?.includes('WagerWiz Props') || dataRow.sharp_filter?.includes('WagerWiz')) {
								sharpBook = 'WagerWiz';
							}

							let backgroundColorClass = selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? 'evtable-row-selected' : 'evtable-row1';
							if (index % 2 !== 0) {
								backgroundColorClass = selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? 'evtable-row-selected' : 'evtable-row2';
							}

							let condition = dataRow.game;
							if (isFree || freeRowLimit != null) {
								condition = true;
							}
							if (dataRow.market?.includes('3-Way')) {
								condition = false;
							}

							if (condition) {
								// if (true) {
								// Calculate if this row should be shifted down
								const shouldShiftDown = selectedRow && selectedRowIndex !== null && index > selectedRowIndex;
								const shiftAmount = shouldShiftDown ? dropdownHeight : 0;
								
								return (
									<>
										<TableRow
											ref={observeRowRef}
											data-link-outcome-id={dataRow._linkOutcomeId || ''}
											data-sharp-outcome-id={dataRow._sharpOutcomeId || ''}
											data-sport={dataRow.sport || dataRow._sport || ''}
											data-outcome-id-no-book={dataRow.outcome_id_normalized_no_book || ''}
											data-market={dataRow.market || ''}
											data-points={dataRow.points || ''}
											data-home-team={dataRow.home_team || ''}
											style={{ 
												filter: freeRowLimit != null ? (index >= freeRowLimit ? 'blur(5px)' : '') : (isFree && index > 0 ? 'blur(5px)' : ''),
												transition: 'all 0.3s ease-in-out',
												cursor: 'pointer',
												transform: `translateY(${shiftAmount}px)`
											}}
											onClick={async (e) => {
												if (selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized) {
													setSelectedRow();
													setSelectedRowIndex(null);
												} else {
													if (dataRow.outcome_id_normalized !== '') {
														setSelectedRow(dataRow);
														setSelectedRowIndex(index); // 👈 Save the index
														
														// Calculate dropdown position
														const rowElement = e.currentTarget;
														const containerRect = tableContainerRef.current.getBoundingClientRect();
														
														// Calculate position relative to the container, accounting for scroll
														const rowOffsetTop = rowElement.offsetTop;
														
														setDropdownPosition({
															top: rowOffsetTop + rowElement.offsetHeight, // Add 8px gap to avoid overlap
															left: 0,
															width: containerRect.width
														});
														
														// Scroll the row to near the top of the viewport with smooth animation
														// Find the scrollable parent container
														let scrollableParent = tableContainerRef.current;
														let foundScrollable = false;
														
														// Traverse up to find the scrollable parent
														while (scrollableParent && scrollableParent !== document.body && scrollableParent !== document.documentElement) {
															const style = window.getComputedStyle(scrollableParent);
															const overflowY = style.overflowY || style.overflow;
															const hasVerticalScroll = scrollableParent.scrollHeight > scrollableParent.clientHeight;
															
															if (hasVerticalScroll && (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')) {
																foundScrollable = true;
																break;
															}
															
															scrollableParent = scrollableParent.parentElement;
														}
														
														// If no scrollable parent found, check window/document
														if (!foundScrollable) {
															scrollableParent = window;
														}
														
														// Calculate the target scroll position to bring row to the top of the viewport
														// Small offset to account for any fixed headers or padding
														const topOffset = 10;
														const rowRect = rowElement.getBoundingClientRect();
														
														// Account for transform shift: if row is currently shifted down by dropdown,
														// it will move up when dropdown closes, so we need to adjust scroll calculation
														const isRowShiftedDown = selectedRow && selectedRowIndex !== null && index > selectedRowIndex;
														const currentShiftAmount = isRowShiftedDown ? dropdownHeight : 0;
														
														// Adjust rowRect.top to account for the transform that will be removed
														// When dropdown closes, row will move up by currentShiftAmount
														const adjustedRowTop = rowRect.top - currentShiftAmount;
														
														// Always scroll to position the row at the top of the viewport
														// The useEffect will fine-tune after the dropdown renders to ensure it's fully visible
														if (scrollableParent === window) {
															// Scroll window - position row at top of viewport
															const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
															// Calculate scroll needed to bring row to topOffset from top of viewport
															// Use adjustedRowTop to account for transform removal
															const targetScrollY = currentScrollY + adjustedRowTop - topOffset;
															
															window.scrollTo({
																top: Math.max(0, targetScrollY),
																behavior: 'smooth'
															});
														} else {
															// Scroll the container element - position row at top of container viewport
															const containerRect = scrollableParent.getBoundingClientRect();
															const relativeTop = adjustedRowTop - containerRect.top + scrollableParent.scrollTop;
															const targetScrollTop = relativeTop - topOffset;
															
															scrollableParent.scrollTo({
																top: Math.max(0, targetScrollTop),
																behavior: 'smooth'
															});
														}
														
														try {
															const relatedOdds = await getRelatedOdds(
																dataRow.outcome_id_normalized_no_book,
																dataRow.sport,
																{ market: dataRow.market, points: dataRow.points, home_team: dataRow.home_team }
															);
															setRelatedOdds(relatedOdds);
														} catch (e) {
															console.error('[Related Odds]', e?.message || e);
															setRelatedOdds([]);
														}
													} else {
														setIsUpgradeModalOpen(true);
													}
												}
											}}
											className={`${backgroundColorClass} ${selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized && 'selectedrow'} evtable-row`}
											id="evtable-row">
											<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', width: '7%', minWidth: '60px' }}>
												{dataRow.league}
											</TableCell>
											<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', width: '9%', minWidth: '90px' }}>
												{dataRow.market}
											</TableCell>

											<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', width: '11%', minWidth: '110px' }}>
												{dataRow.outcome_id_normalized !== '' && <GameDiv dataRow={dataRow} selected={selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized} />}
											</TableCell>
											<TableCell
												style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', textAlign: 'center', width: '9%', minWidth: '90px' }}>
												{betName}
											</TableCell>
											<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', width: '8%', minWidth: '80px', overflow: 'visible' }}>
												<BookAndLink isSharp={false} book={dataRow.sportsbook_dumb} link={link} selected={selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized} />
											</TableCell>
											<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', width: '9%', minWidth: '90px' }}>
												<div
													style={{
														alignItems: 'center',
														display: 'grid',
														gridTemplateColumns: '60% 40%',
														backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '',
													}}>
													<BetPrice price={betPrice} />
													<BetPriceDirection direction={bet_price_direction_dumb} selected={selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized} isLoading={isPriceHistoryLoadingForRow} />
												</div>
											</TableCell>
											<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', width: '9%', minWidth: '90px', overflow: 'visible' }}>
												<div className="flex flex-col items-start" style={{ backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', overflow: 'visible' }}>
													<BookAndLink isSharp={true} book={sharpBook} link={sharpLink} selected={selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized} />
													{limit && sharpBook !== 'WagerWiz' && dataRow.sharp_filter && dataRow.sharp_filter.length === 1 && (
														<div style={{ 
															fontSize: '10px', 
															color: '#ffffff', 
															marginTop: '4px',
															backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : ''
														}}>
															Limit: <span style={{ color: limit < 1000 ? '#ff4444' : limit < 2500 ? '#ffaa00' : '#44ff44' }}>${limit?.toLocaleString()}</span>
														</div>
													)}
												</div>
											</TableCell>
											<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', width: '11%', minWidth: '110px' }}>
												<div className="flex flex-row">
													<div
														style={{
															alignItems: 'center',
															display: 'grid',
															gridTemplateColumns: '60% 40%',
															backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '',
														}}>
														{/* Swap: Show Sharp Price here */}
														<Tooltip title={`Sharp price: ${sharpPrice}`}>
															<div>
																<BetPrice
																	price={
																		(() => {
																			let deviggedOdds;
																			if (dataRow.market === 'Point Spread' || dataRow.market === 'Asian Handicap') {
																				// For point spread bets, use the side that corresponds to the actual bet
																				if (dataRow.ev_spread === dataRow.ev_over_positive) {
																					deviggedOdds = devigOddsProbit(Number(sharpPriceOver), Number(sharpPriceUnder)).overOdds;
																				} else if (dataRow.ev_spread === dataRow.ev_under_positive) {
																					deviggedOdds = devigOddsProbit(Number(sharpPriceOver), Number(sharpPriceUnder)).underOdds;
																				} else if (dataRow.ev_spread === dataRow.ev_over_negative) {
																					deviggedOdds = devigOddsProbit(Number(sharpPriceOver), Number(sharpPriceUnder)).overOdds;
																				} else if (dataRow.ev_spread === dataRow.ev_under_negative) {
																					deviggedOdds = devigOddsProbit(Number(sharpPriceOver), Number(sharpPriceUnder)).underOdds;
																				}
																				
																				// For point spread bets, ensure the fair value sign matches the bet price sign
																				const betPriceNum = parseFloat(betPrice.replace(/[+\-]/g, ''));
																				const betPriceSign = betPrice.includes('-') ? -1 : 1;
																				const deviggedSign = deviggedOdds < 0 ? -1 : 1;
																				
																				// If signs don't match, reverse the devigged odds
																				if (betPriceSign !== deviggedSign) {
																					deviggedOdds = -deviggedOdds;
																				}
																			} else {
																				// For non-point spread bets, use the existing logic
																				deviggedOdds = isFairValueOver
																					? devigOddsProbit(Number(sharpPriceOver), Number(sharpPriceUnder)).overOdds
																					: devigOddsProbit(Number(sharpPriceOver), Number(sharpPriceUnder)).underOdds;
																			}
																			return deviggedOdds < 0 ? deviggedOdds.toString() : '+' + deviggedOdds;
																		})()
																	}
																/>
															</div>
														</Tooltip>

														<div style={{ backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '' }} className="ml-2">
															<BetPriceDirection direction={bet_price_direction_sharp} selected={selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized} isLoading={isPriceHistoryLoadingForRow} />
														</div>
													</div>
												</div>
											</TableCell>
											
											<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', width: '8%', minWidth: '60px' }}>
												<EVPercent ev={ev_value} />
											</TableCell>
											<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', width: '7%', minWidth: '60px' }}>
												<Tooltip
													style={{ backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '' }}
													title={`Quarter Kelly: ${quarter_kelly?.toFixed(2)}u`}
													componentsProps={{
														tooltip: {
															sx: {
																fontSize: '1.1rem',
															},
														},
													}}>
													{(() => {
														const betSize = (bankroll * quarter_kelly) / 100;
														if (betSize < 100) {
															return `$${betSize.toFixed(2)}`;
														} else {
															return `$${Math.round(betSize).toLocaleString()}`;
														}
													})()}
												</Tooltip>
											</TableCell>
											{process.env.REACT_APP_CONFIDENCE !== 'false' && (
												<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', width: '10%', minWidth: '72px', textAlign: 'center', overflow: 'hidden', boxSizing: 'border-box' }}>
													<ConfidenceDisplay 
														confidence={confidenceData.confidence} 
														selected={selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized}
														calculationBreakdown={confidenceData.breakdown}
														isLoading={isPriceHistoryLoadingForRow}
													/>
												</TableCell>
											)}
											<TableCell
												style={{
													backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '',
													width: '18%',
													minWidth: '120px',
													overflow: 'visible',
												}}>
												<div
													data-popover-trigger
													style={{ width: '100%', backgroundColor: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#363844' : '', overflow: 'visible', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}
													onMouseEnter={(e) => {
														// Clear any pending timeout
														if (hoverTimeoutRef.current) {
															clearTimeout(hoverTimeoutRef.current);
															hoverTimeoutRef.current = null;
														}
														// Show popover when hovering over checkbox or amount display (if bet is placed)
														if (placedBets?.some((placedBet) => placedBet.id === dataRow.outcome_id_normalized)) {
															const checkbox = e.currentTarget.querySelector('.MuiCheckbox-root');
															if (checkbox) {
																setAmountPopoverAnchor(checkbox);
																setAmountPopoverRowId(dataRow.outcome_id_normalized);
															}
														}
													}}
													onMouseLeave={(e) => {
														// Check if mouse is moving to popover
														const relatedTarget = e.relatedTarget;
														const isMovingToPopover = relatedTarget instanceof Node && (
															popoverRef.current?.contains(relatedTarget) ||
															relatedTarget.closest('[role="presentation"]')?.querySelector('[class*="MuiPopover"]')
														);
														
														if (!isMovingToPopover) {
															// Delay closing to allow moving to popover
															hoverTimeoutRef.current = setTimeout(() => {
																setAmountPopoverAnchor(null);
																setAmountPopoverRowId(null);
																hoverTimeoutRef.current = null;
															}, 150);
														}
													}}
													onClick={(e) => {
														// If clicking on the checkbox area (not the popover), allow the click to pass through
														const isCheckbox = e.target.closest('.MuiCheckbox-root');
														if (isCheckbox) {
															// Close popover when clicking checkbox area
															if (hoverTimeoutRef.current) {
																clearTimeout(hoverTimeoutRef.current);
																hoverTimeoutRef.current = null;
															}
															setAmountPopoverAnchor(null);
															setAmountPopoverRowId(null);
														}
													}}
												>
													<Tooltip title={isMaster ? 'Bet Placed (must upgrade plan to use this feature)' : ''}>
														<Checkbox
															onClick={(e) => {
																e.stopPropagation();
																// Close popover when clicking checkbox
																if (hoverTimeoutRef.current) {
																	clearTimeout(hoverTimeoutRef.current);
																}
																setAmountPopoverAnchor(null);
																setAmountPopoverRowId(null);
															}}
															sx={{
																color: selectedRow?.outcome_id_normalized === dataRow.outcome_id_normalized ? '#7a7e96' : '#363844', // Default border color when unchecked
																'&.Mui-checked': {
																	color: '#28792a', // Border and check color when checked
																},
																'&:hover': {
																	color: '#5dcc60', // Border color on hover
																},
																position: 'relative',
																zIndex: 10, // Ensure checkbox is above popover
															}}
															onChange={async (e) => {
																if (isMaster || !user?.email) {
																	setIsUpgradeModalOpen(true);
																} else {
																	if (dataRow.outcome_id_normalized !== '') {
																		if (e.target.checked) {
																			await setBetPlaced(dataRow.outcome_id_normalized, user.email);
																			setPlacedBets([...placedBets, { id: dataRow.outcome_id_normalized, time: new Date() }]);
																		} else {
																			const copy = JSON.parse(JSON.stringify(placedBets));
																			const newCopy = removeObjectWithProperty(copy, 'id', dataRow.outcome_id_normalized);
																			setPlacedBets(newCopy);
																			await removeBetPlaced(dataRow.outcome_id_normalized, user.email);
																		}
																	}
																}

																// console.log('e: ', e.target.checked, dataRow);
															}}
															checked={placedBets?.some((placedBet) => placedBet.id === dataRow.outcome_id_normalized)}
														/>
													</Tooltip>
													{placedBets?.some((placedBet) => placedBet.id === dataRow.outcome_id_normalized) && (
														<div 
															style={{ color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '4px', width: '100%' }}
															onMouseEnter={(e) => {
																// Clear any pending timeout when hovering over amount display
																if (hoverTimeoutRef.current) {
																	clearTimeout(hoverTimeoutRef.current);
																	hoverTimeoutRef.current = null;
																}
																// Ensure popover is open when hovering over amount display
																if (amountPopoverRowId !== dataRow.outcome_id_normalized) {
																	const checkbox = e.currentTarget.closest('[data-popover-trigger]')?.querySelector('.MuiCheckbox-root');
																	if (checkbox) {
																		setAmountPopoverAnchor(checkbox);
																		setAmountPopoverRowId(dataRow.outcome_id_normalized);
																	}
																}
															}}
														>
															<div style={{ fontSize: '13px', fontWeight: 500 }}>
																${placedBets?.find((placedbet) => placedbet.id === dataRow.outcome_id_normalized)?.value || '0.00'}
															</div>
															<div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px' }}>
																{(percentOfTotalBankroll * 100).toFixed(2)}%
															</div>
														</div>
													)}
													<Popover
														open={amountPopoverAnchor !== null && amountPopoverRowId === dataRow.outcome_id_normalized}
														anchorEl={amountPopoverAnchor}
														onClose={(e, reason) => {
															// Clear any pending timeout
															if (hoverTimeoutRef.current) {
																clearTimeout(hoverTimeoutRef.current);
															}
															// Close on backdrop click or escape key
															setAmountPopoverAnchor(null);
															setAmountPopoverRowId(null);
														}}
														anchorOrigin={{
															vertical: 'bottom',
															horizontal: 'left',
														}}
														transformOrigin={{
															vertical: 'top',
															horizontal: 'left',
														}}
														disableRestoreFocus
														onClick={(e) => {
															// Allow clicks inside the popover
															e.stopPropagation();
														}}
														slotProps={{
															root: {
																onClick: (e) => {
																	// Don't close if clicking inside the popover paper
																	if (popoverRef.current && popoverRef.current.contains(e.target)) {
																		return;
																	}
																	// Close if clicking outside
																	if (hoverTimeoutRef.current) {
																		clearTimeout(hoverTimeoutRef.current);
																	}
																	setAmountPopoverAnchor(null);
																	setAmountPopoverRowId(null);
																},
																style: {
																	pointerEvents: 'none', // Allow clicks to pass through to checkbox
																}
															}
														}}
														PaperProps={{
															ref: popoverRef,
															onMouseEnter: () => {
																// Cancel any pending close timeout when hovering over popover
																if (hoverTimeoutRef.current) {
																	clearTimeout(hoverTimeoutRef.current);
																	hoverTimeoutRef.current = null;
																}
															},
															onMouseLeave: (e) => {
																// Check if mouse is moving back to trigger area
																const relatedTarget = e.relatedTarget;
																const isMovingToTrigger = relatedTarget instanceof Node && (
																	relatedTarget.closest('[data-popover-trigger]') ||
																	relatedTarget.closest('.MuiCheckbox-root')
																);
																
																if (!isMovingToTrigger) {
																	// Close when leaving popover and not moving to trigger
																	if (hoverTimeoutRef.current) {
																		clearTimeout(hoverTimeoutRef.current);
																		hoverTimeoutRef.current = null;
																	}
																	setAmountPopoverAnchor(null);
																	setAmountPopoverRowId(null);
																}
															},
															onClick: (e) => {
																// Stop propagation so clicks inside popover don't close it
																e.stopPropagation();
															},
															style: {
																backgroundColor: '#1a1d23',
																padding: '12px',
																borderRadius: '8px',
																border: '1px solid rgba(255, 255, 255, 0.1)',
																pointerEvents: 'auto', // Popover content should be clickable
															}
														}}
													>
														<div 
															onMouseEnter={() => {
																// Cancel the timeout when hovering over popover content
																if (hoverTimeoutRef.current) {
																	clearTimeout(hoverTimeoutRef.current);
																	hoverTimeoutRef.current = null;
																}
															}}
															style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '150px' }}
														>
															<div style={{ fontSize: '12px', color: '#b1b6c3', marginBottom: '4px' }}>Amount ($)</div>
															<input
																onClick={(e) => {
																	e.stopPropagation();
																}}
																type="number"
																onKeyDown={(e) => {
																	if (e.key === 'Enter') {
																		// Close popover when Enter is pressed
																		if (hoverTimeoutRef.current) {
																			clearTimeout(hoverTimeoutRef.current);
																		}
																		setAmountPopoverAnchor(null);
																		setAmountPopoverRowId(null);
																		// Remove focus from input
																		e.target.blur();
																	}
																}}
																onChange={async (e) => {
																	if (!user?.email) return;
																	const copy = JSON.parse(JSON.stringify(placedBets));
																	for (const placedbet of copy) {
																		if (placedbet.id === dataRow.outcome_id_normalized) {
																			placedbet.value = e.target.value;
																		}
																	}
																	setPlacedBets(copy);
																	await setBetPlaced(dataRow.outcome_id_normalized, user.email, e.target.value);
																}}
																value={placedBets?.find((placedbet) => placedbet.id === dataRow.outcome_id_normalized)?.value || ''}
																style={{
																	width: '100%',
																	padding: '8px',
																	border: '1px solid rgba(255, 255, 255, 0.2)',
																	outline: 'none',
																	backgroundColor: '#363844',
																	color: '#b1b6c3',
																	borderRadius: '6px',
																	fontSize: '14px',
																	boxSizing: 'border-box',
																}}
																autoFocus
															/>
															<div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>
																{(percentOfTotalBankroll * 100).toFixed(2)}% of bankroll
															</div>
														</div>
													</Popover>
												</div>
											</TableCell>
										</TableRow>
									</>
								);
							}
							return null;
						})
					)}
				</TableBody>
			</Table>
			
			{/* Loading Overlay */}
			{isLoading && (
				<div style={{
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(23, 25, 29, 0.8)',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					zIndex: 10,
					borderRadius: '16px'
				}}>
					<img 
						style={{ 
							width: isMobileProp ? '200px' : '300px',
							marginBottom: '20px'
						}} 
						src={loadingIcon} 
						alt="loading icon" 
					/>
					{/* <div style={{ 
						color: '#eff1f5', 
						fontSize: isMobileProp ? '16px' : '18px',
						textAlign: 'center'
					}}>
						Scanning for +EV opportunities...
					</div> */}
				</div>
			)}
			
			{/* Related Odds Dropdown Overlay */}
			{selectedRow && relatedOdds && (() => {
				// Calculate the same variables that were calculated in the map function
				let overSide = 'bet_price_over';
				let underSide = 'bet_price_under';
				let sharpPriceOver = '';
				let sharpPriceUnder = '';
				let sharpPrice = '';
				let isSpread = false;
				// For spreads, determine isOver based on which side was selected (ev_spread)
				// "over" = home team side (ev_over_positive or ev_over_negative)
				// "under" = away team side (ev_under_positive or ev_under_negative)
				let isOver = selectedRow.ev_over > selectedRow.ev_under;

				if (selectedRow.market === 'Point Spread' || selectedRow.market === 'Asian Handicap') {
					isSpread = true;
					// For spreads, isOver is true if the selected side is the home team (over) side
					isOver = (selectedRow.ev_spread === selectedRow.ev_over_positive || 
					          selectedRow.ev_spread === selectedRow.ev_over_negative);
					if (selectedRow.ev_spread === selectedRow.ev_over_positive) {
						sharpPriceOver = formatPrice(selectedRow.bet_price_over_positive_sharp);
						sharpPriceUnder = formatPrice(selectedRow.bet_price_under_negative_sharp);
						sharpPrice = `${formatPrice(selectedRow.bet_price_over_positive_sharp) || ''} / ${formatPrice(selectedRow.bet_price_under_negative_sharp) || ''}`;
						overSide = 'bet_price_over_positive';
						underSide = 'bet_price_under_negative';
					} else if (selectedRow.ev_spread === selectedRow.ev_under_positive) {
						sharpPriceOver = formatPrice(selectedRow.bet_price_under_positive_sharp);
						sharpPriceUnder = formatPrice(selectedRow.bet_price_over_negative_sharp);
						sharpPrice = `${formatPrice(selectedRow.bet_price_under_positive_sharp) || ''} / ${formatPrice(selectedRow.bet_price_over_negative_sharp) || ''}`;
						overSide = 'bet_price_over_negative';
						underSide = 'bet_price_under_positive';
					} else if (selectedRow.ev_spread === selectedRow.ev_over_negative) {
						sharpPriceOver = formatPrice(selectedRow.bet_price_over_negative_sharp);
						sharpPriceUnder = formatPrice(selectedRow.bet_price_under_positive_sharp);
						sharpPrice = `${formatPrice(selectedRow.bet_price_over_negative_sharp) || ''} / ${formatPrice(selectedRow.bet_price_under_positive_sharp) || ''}`;
						overSide = 'bet_price_over_negative';
						underSide = 'bet_price_under_positive';
					} else if (selectedRow.ev_spread === selectedRow.ev_under_negative) {
						sharpPriceOver = formatPrice(selectedRow.bet_price_over_positive_sharp);
						sharpPriceUnder = formatPrice(selectedRow.bet_price_under_negative_sharp);
						sharpPrice = `${formatPrice(selectedRow.bet_price_over_positive_sharp) || ''} / ${formatPrice(selectedRow.bet_price_under_negative_sharp) || ''}`;
						overSide = 'bet_price_over_positive';
						underSide = 'bet_price_under_negative';
					}
				} else if (selectedRow.market === 'Moneyline') {
					if (selectedRow.ev_over > selectedRow.ev_under) {
						sharpPriceOver = formatPrice(selectedRow.bet_price_over_sharp);
						sharpPriceUnder = formatPrice(selectedRow.bet_price_under_sharp);
						sharpPrice = `${formatPrice(selectedRow.bet_price_over_sharp)} / ${formatPrice(selectedRow.bet_price_under_sharp)}`;
					} else {
						sharpPriceOver = formatPrice(selectedRow.bet_price_over_sharp);
						sharpPriceUnder = formatPrice(selectedRow.bet_price_under_sharp);
						sharpPrice = `${formatPrice(selectedRow.bet_price_under_sharp)} / ${formatPrice(selectedRow.bet_price_over_sharp)}`;
					}
				} else {
					if (selectedRow.ev_over > selectedRow.ev_under) {
						sharpPriceOver = formatPrice(selectedRow.bet_price_over_sharp);
						sharpPriceUnder = formatPrice(selectedRow.bet_price_under_sharp);
						sharpPrice = `${formatPrice(selectedRow.bet_price_over_sharp)} / ${formatPrice(selectedRow.bet_price_under_sharp)}`;
					} else {
						sharpPriceOver = formatPrice(selectedRow.bet_price_over_sharp);
						sharpPriceUnder = formatPrice(selectedRow.bet_price_under_sharp);
						sharpPrice = `${formatPrice(selectedRow.bet_price_under_sharp)} / ${formatPrice(selectedRow.bet_price_over_sharp)}`;
					}
				}

				return (
					<div 
						ref={dropdownRef}
						className="related-odds-overlay"
						style={{
							top: `${dropdownPosition.top}px`,
							left: `${dropdownPosition.left}px`,
							width: `${dropdownPosition.width}px`
						}}
					>
						<RelatedOddsDropdown
							relatedOdds={relatedOdds}
							isOver={isOver}
							sportsbooksToHighlight={[...(selectedRow.sharp_book ? [...new Set(selectedRow.sharp_book.split(',').map(b => b.trim()))] : []), selectedRow.sportsbook_dumb]}
							sportsbook_dumb={selectedRow.sportsbook_dumb}
							outcome_id_normalized={selectedRow?.outcome_id_normalized}
							sharpBook={selectedRow.sharp_book}
							sharpFilter={selectedRow.sharp_filter}
							fairValue={selectedRow.devigged1 || selectedRow.devigged3 || selectedRow.devigged5}
							selectedRow={selectedRow}
							overSide={overSide}
							underSide={underSide}
							tutorial={tutorial}
							isLive={isLive}
							sharpPriceOver={sharpPriceOver}
							sharpPriceUnder={sharpPriceUnder}
							isSpread={isSpread}
							sharpPrice={sharpPrice}
							onClose={() => {
								setSelectedRow();
								setSelectedRowIndex(null);
							}}
						/>
					</div>
				);
			})()}
			</div>
		);
	} else {
		return (
			<div style={{ 
				justifyItems: 'center',
				padding: '16px',
				background: 'linear-gradient(135deg, #17191d 0%, #1a1d23 100%)',
				borderRadius: '16px',
				margin: '0 4px',
				position: 'relative'
			}}>
				<UpgradeModal isOpen={isUpgradeModalOpen} setIsOpen={setIsUpgradeModalOpen} />

				{isLoading ? (
					// Show loading skeleton cards for mobile
					Array.from({ length: 3 }).map((_, index) => (
						<div key={`mobile-loading-${index}`} style={{
							width: "-webkit-fill-available", 
							paddingLeft: "2px", 
							paddingRight: "2px",
							marginBottom: "16px",
							transition: "all 0.2s ease-in-out"
						}} className="mb-3">
							<div style={{
								background: 'linear-gradient(135deg, #17191d 0%, #1a1d23 100%)',
								borderRadius: '12px',
								padding: '16px',
								border: '1px solid #363844'
							}}>
								<div style={{ 
									height: '20px', 
									backgroundColor: '#363844', 
									borderRadius: '4px',
									animation: 'pulse 1.5s ease-in-out infinite',
									marginBottom: '12px'
								}} />
								<div style={{ 
									height: '16px', 
									backgroundColor: '#363844', 
									borderRadius: '4px',
									animation: 'pulse 1.5s ease-in-out infinite',
									marginBottom: '8px',
									width: '60%'
								}} />
								<div style={{ 
									height: '16px', 
									backgroundColor: '#363844', 
									borderRadius: '4px',
									animation: 'pulse 1.5s ease-in-out infinite',
									width: '40%'
								}} />
							</div>
						</div>
					))
				) : (
					augmentedRows?.slice(0, 150).map((dataRow, index) => {
					let betName = '';
					let betPrice = '';
					let sharpPrice = '';
					let sharpPriceOver = '';
					let sharpPriceUnder = '';
					let quarter_kelly = 0;
					let ev_value = 0;
					let link = '';
					let sharpLink = '';
					let bet_price_direction_dumb = 0;
					let bet_price_direction_sharp = 0;

					let limit;
					if (dataRow.limit_sharp) {
						try {
							if (Array.isArray(dataRow.limit_sharp)) {
								for (const l of dataRow.limit_sharp) {
									if (l?.max) {
										limit = l.max;
									}
								}
							} else {
								limit = dataRow.limit_sharp?.max;
							}
						} catch (e) {
							console.log('e: ', e);
						}
					}

					let overSide = 'bet_price_over';
					let underSide = 'bet_price_under';

					// Handle cases where outcome_id_normalized might be undefined
					const outcomeIdSplit = dataRow.outcome_id_normalized ? dataRow.outcome_id_normalized.split(':') : [];

											if (dataRow.market === 'Point Spread' || dataRow.market === 'Asian Handicap') {
						if (dataRow.ev_spread === dataRow.ev_over_positive) {
							ev_value = dataRow.ev_over_positive * 100;
							betName = `${dataRow.game?.home_team_display} +${dataRow.points || ''}`;
							betPrice = formatPrice(dataRow.bet_price_over_positive_dumb);
							sharpPrice = `${formatPrice(dataRow.bet_price_over_positive_sharp) || ''} / ${formatPrice(dataRow.bet_price_under_negative_sharp) || ''}`;
							sharpPriceOver = formatPrice(dataRow.bet_price_over_positive_sharp);
							sharpPriceUnder = formatPrice(dataRow.bet_price_under_negative_sharp);
							quarter_kelly = dataRow.quarter_kelly_over_positive;
									link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
									sharpLink = formatSharpDeepLink(dataRow.sharpLink);
							bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_history_over_positive);
							bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_history_over_positive);

							overSide = 'bet_price_over_positive';
							underSide = 'bet_price_under_negative';
						} else if (dataRow.ev_spread === dataRow.ev_under_positive) {
							ev_value = dataRow.ev_under_positive * 100;
							betName = `${dataRow.game?.away_team_display} +${dataRow.points || ''}`;
							betPrice = formatPrice(dataRow.bet_price_under_positive_dumb);
							sharpPrice = `${formatPrice(dataRow.bet_price_under_positive_sharp) || ''} / ${formatPrice(dataRow.bet_price_over_negative_sharp) || ''}`;
							sharpPriceOver = formatPrice(dataRow.bet_price_under_positive_sharp);
							sharpPriceUnder = formatPrice(dataRow.bet_price_over_negative_sharp);
							quarter_kelly = dataRow.quarter_kelly_under_positive;
									link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
									sharpLink = formatSharpDeepLink(dataRow.sharpLink);
							bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_history_under_positive);
							bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_history_under_positive);

							overSide = 'bet_price_over_negative';
							underSide = 'bet_price_under_positive';
						} else if (dataRow.ev_spread === dataRow.ev_over_negative) {
							ev_value = dataRow.ev_over_negative * 100;
							betName = `${dataRow.game?.home_team_display} -${dataRow.points || ''}`;
							betPrice = formatPrice(dataRow.bet_price_over_negative_dumb);
							sharpPrice = `${formatPrice(dataRow.bet_price_over_negative_sharp) || ''} / ${formatPrice(dataRow.bet_price_under_positive_sharp) || ''}`;
							sharpPriceOver = formatPrice(dataRow.bet_price_over_negative_sharp);
							sharpPriceUnder = formatPrice(dataRow.bet_price_under_positive_sharp);
							quarter_kelly = dataRow.quarter_kelly_over_negative;
									link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
									sharpLink = formatSharpDeepLink(dataRow.sharpLink);
							bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_history_over_negative);
							bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_history_over_negative);

							overSide = 'bet_price_over_negative';
							underSide = 'bet_price_under_positive';
						} else if (dataRow.ev_spread === dataRow.ev_under_negative) {
							ev_value = dataRow.ev_under_negative * 100;
							betName = `${dataRow.game?.away_team_display} -${dataRow.points || ''}`;
							betPrice = formatPrice(dataRow.bet_price_under_negative_dumb);
							sharpPrice = `${formatPrice(dataRow.bet_price_under_negative_sharp) || ''} / ${formatPrice(dataRow.bet_price_over_positive_sharp) || ''}`;
							sharpPriceOver = formatPrice(dataRow.bet_price_over_negative_sharp);
							sharpPriceUnder = formatPrice(dataRow.bet_price_under_positive_sharp);
							quarter_kelly = dataRow.quarter_kelly_under_negative;
									link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
									sharpLink = formatSharpDeepLink(dataRow.sharpLink);
							bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_history_under_negative);
							bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_history_under_negative);

							overSide = 'bet_price_under_negative';
							underSide = 'bet_price_over_positive';
						}
					} else if (dataRow.market === 'Moneyline') {
						if (dataRow.ev_over > dataRow.ev_under) {
							betName = dataRow.game?.home_team_display;
							betPrice = formatPrice(dataRow.bet_price_over_dumb);
							sharpPrice = `${formatPrice(dataRow.bet_price_over_sharp)} / ${formatPrice(dataRow.bet_price_under_sharp)}`;
							sharpPriceOver = formatPrice(dataRow.bet_price_over_sharp);
							sharpPriceUnder = formatPrice(dataRow.bet_price_under_sharp);
							quarter_kelly = dataRow.quarter_kelly_over;
							ev_value = dataRow.ev_over * 100;
							link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
							sharpLink = formatSharpDeepLink(dataRow.sharpLink);
							bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_over_history);
							bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_over_history);
						} else {
							betName = dataRow.game?.away_team_display;
							betPrice = formatPrice(dataRow.bet_price_under_dumb);
							sharpPrice = `${formatPrice(dataRow.bet_price_under_sharp)} / ${formatPrice(dataRow.bet_price_over_sharp)}`;
							sharpPriceOver = formatPrice(dataRow.bet_price_over_sharp);
							sharpPriceUnder = formatPrice(dataRow.bet_price_under_sharp);
							quarter_kelly = dataRow.quarter_kelly_under;
							ev_value = dataRow.ev_under * 100;
							link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
							sharpLink = formatSharpDeepLink(dataRow.sharpLink);
							bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_under_history);
							bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_under_history);
						}
					} else if (dataRow.ev_over > dataRow.ev_under) {
						// console.log('dataRow: ', dataRow);
						ev_value = dataRow.ev_over * 100;
						betName = `${outcomeIdSplit[2] || ''} over ${outcomeIdSplit[3] || ''}`;
						betPrice = formatPrice(dataRow.bet_price_over_dumb);
						sharpPrice = `${formatPrice(dataRow.bet_price_over_sharp)} / ${formatPrice(dataRow.bet_price_under_sharp)}`;
						sharpPriceOver = formatPrice(dataRow.bet_price_over_sharp);
						sharpPriceUnder = formatPrice(dataRow.bet_price_under_sharp);
						quarter_kelly = dataRow.quarter_kelly_over;
						link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);

						sharpLink = formatSharpDeepLink(dataRow.sharpLink);
						bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_over_history);
						bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_over_history);
					} else {
						betName = `${outcomeIdSplit[2] || ''} under ${outcomeIdSplit[3] || ''}`;
						betPrice = formatPrice(dataRow.bet_price_under_dumb);
						sharpPrice = `${formatPrice(dataRow.bet_price_under_sharp)} / ${formatPrice(dataRow.bet_price_over_sharp)}`;
						sharpPriceOver = formatPrice(dataRow.bet_price_over_sharp);
						sharpPriceUnder = formatPrice(dataRow.bet_price_under_sharp);
						quarter_kelly = dataRow.quarter_kelly_under;
						ev_value = dataRow.ev_under * 100;
						link = formatDeepLink(dataRow.link || dataRow.deep_link?.desktop);
						sharpLink = formatSharpDeepLink(dataRow.sharpLink);
						bet_price_direction_dumb = getLineMovementDirection(dataRow.link?.bet_price_under_history);
						bet_price_direction_sharp = getLineMovementDirection(dataRow.sharpLink?.bet_price_under_history);
					}

					// Check if price history is still loading for this row
					const isPriceHistoryLoadingForRowMobile = !isPriceHistoryLoaded(dataRow);

					// console.log('betName: ', betName, outcomeIdSplit);

					let sharpBook = dataRow.sharp_book;

					if (dataRow.sharp_filter?.includes('WagerWiz Mains') || dataRow.sharp_filter?.includes('WagerWiz Props')) {
						sharpBook = 'WagerWiz';
					}

					let condition = dataRow.game;
					if (isFree || freeRowLimit != null) {
						if (dataRow.outcome_id_normalized !== '') {
							condition = true;
						}
					}
					if (dataRow.market?.includes('3-Way')) {
						condition = false;
					}

					if (condition) {
						return (
							<div
								ref={observeRowRef}
								data-link-outcome-id={dataRow._linkOutcomeId || ''}
								data-sharp-outcome-id={dataRow._sharpOutcomeId || ''}
								data-sport={dataRow.sport || dataRow._sport || ''}
								data-outcome-id-no-book={dataRow.outcome_id_normalized_no_book || ''}
								data-market={dataRow.market || ''}
								data-points={dataRow.points || ''}
								data-home-team={dataRow.home_team || ''}
								style={{
								width: "-webkit-fill-available", 
								paddingLeft: "2px", 
								paddingRight: "2px",
								marginBottom: "16px",
								transition: "all 0.2s ease-in-out",
								filter: freeRowLimit != null && index >= freeRowLimit ? 'blur(5px)' : '',
							}} className="mb-3">
								<MobileEVCard
									sharpBook={sharpBook}
									sharpLink={sharpLink}
									betPrice={betPrice}
									sharpPrice={sharpPrice}
									limit={limit}
									quarter_kelly={quarter_kelly}
									ev_value={ev_value}
									link={link}
									bet_price_direction_dumb={bet_price_direction_dumb}
									bet_price_direction_sharp={bet_price_direction_sharp}
									isPriceHistoryLoading={isPriceHistoryLoadingForRowMobile}
									betName={betName}
									dataRow={dataRow}
									relatedOdds={relatedOdds}
									setRelatedOdds={setRelatedOdds}
									preloadedRelatedOdds={preloadedRelatedOdds}
									isPreloadingData={isPreloadingData}
									setSelectedRow={setSelectedRow}
									selectedRowIndex={selectedRowIndex}
									setSelectedRowIndex={setSelectedRowIndex}
									index={index}
									setIsUpgradeModalOpen={setIsUpgradeModalOpen}
									selectedRow={selectedRow}
									overSide={overSide}
									underSide={underSide}
									isLive={isLive}
									sharpPriceOver={sharpPriceOver}
									sharpPriceUnder={sharpPriceUnder}
									user={user}
									isMaster={isMaster}
									placedBets={placedBets}
									setPlacedBets={setPlacedBets}
									bankroll={bankroll}
									sharpFilter={sharpFilter}
								/>
							</div>
						);
					}
					return null;
				})
				)}
				
				{/* Loading Overlay for Mobile */}
				{isLoading && (
					<div style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(23, 25, 29, 0.8)',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 10,
						borderRadius: '16px'
					}}>
						<img 
							style={{ 
								width: '200px',
								marginBottom: '20px'
							}} 
							src={loadingIcon} 
							alt="loading icon" 
						/>
						{/* <div style={{ 
							color: '#eff1f5', 
							fontSize: '16px',
							textAlign: 'center'
						}}>
							Scanning for +EV opportunities...
						</div> */}
					</div>
				)}
			</div>
		);
	}
};
